import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import AuthContext from '../context/AuthContext';
import Base from '../components/Base';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
    Upload, CheckCircle, Edit, RefreshCw, Loader2,
    Image as ImageIcon, Video, Eye, Trash2, Save, ArrowLeft,
    X, Plus, Palette, Type, FileText, Camera, Film,
    ShoppingBag, Star, Grid, Layout, Zap, Move, ChevronDown,
    ChevronUp, PlayCircle, ImagePlus, Sparkles, Layers,
    Settings, Monitor, Smartphone, Tablet, Heart, Share2,
    Download, ArrowRight, Check, AlertCircle, Info,
    Calendar, Users, Leaf, Wrench, Package, BookOpen,
    Quote, MapPin, Mail
} from 'lucide-react';

// Enhanced UX Components
import {
    TemplateSelector,
    EnhancedTemplateBuilder
} from '../components/TemplateBuilder';

// Dynamic Category Options based on Gender and Type
const CATEGORY_MAPPING = {
    // Men + Indian Wear
    'Men_Indianwear': [
        { value: 'All', label: 'All Indian Wear' },
        { value: 'Kurtas', label: 'Kurtas' },
        { value: 'Sherwanis', label: 'Sherwanis' },
        { value: 'Dhotis', label: 'Dhotis' },
        { value: 'Nehru Jackets', label: 'Nehru Jackets' },
        { value: 'Indo Western', label: 'Indo Western' },
        { value: 'Bandhgala', label: 'Bandhgala' },
        { value: 'Pathani Suits', label: 'Pathani Suits' },
        { value: 'Ethnic Jackets', label: 'Ethnic Jackets' },
        { value: 'Traditional Sets', label: 'Traditional Sets' }
    ],

    // Men + Western Wear
    'Men_Westernwear': [
        { value: 'All', label: 'All Western Wear' },
        { value: 'Shirts', label: 'Shirts' },
        { value: 'T-Shirts', label: 'T-Shirts' },
        { value: 'Jeans', label: 'Jeans' },
        { value: 'Trousers', label: 'Trousers' },
        { value: 'Blazers', label: 'Blazers' },
        { value: 'Suits', label: 'Suits' },
        { value: 'Shorts', label: 'Shorts' },
        { value: 'Hoodies', label: 'Hoodies' },
        { value: 'Sweaters', label: 'Sweaters' },
        { value: 'Jackets', label: 'Jackets' },
        { value: 'Polo Shirts', label: 'Polo Shirts' }
    ],

    // Women + Indian Wear
    'Women_Indianwear': [
        { value: 'All', label: 'All Indian Wear' },
        { value: 'Sarees', label: 'Sarees' },
        { value: 'Lehenga Choli', label: 'Lehenga Choli' },
        { value: 'Salwar Kameez', label: 'Salwar Kameez' },
        { value: 'Anarkali Suits', label: 'Anarkali Suits' },
        { value: 'Churidar', label: 'Churidar' },
        { value: 'Palazzo Sets', label: 'Palazzo Sets' },
        { value: 'Kurtis', label: 'Kurtis' },
        { value: 'Sharara Sets', label: 'Sharara Sets' },
        { value: 'Gharara Sets', label: 'Gharara Sets' },
        { value: 'Indo Western Gowns', label: 'Indo Western Gowns' },
        { value: 'Ethnic Crop Tops', label: 'Ethnic Crop Tops' },
        { value: 'Dupattas', label: 'Dupattas' }
    ],

    // Women + Western Wear
    'Women_Westernwear': [
        { value: 'All', label: 'All Western Wear' },
        { value: 'Dresses', label: 'Dresses' },
        { value: 'Tops', label: 'Tops' },
        { value: 'Blouses', label: 'Blouses' },
        { value: 'Skirts', label: 'Skirts' },
        { value: 'Pants', label: 'Pants' },
        { value: 'Jeans', label: 'Jeans' },
        { value: 'Jackets', label: 'Jackets' },
        { value: 'Jumpsuits', label: 'Jumpsuits' },
        { value: 'Co-ord Sets', label: 'Co-ord Sets' },
        { value: 'Blazers', label: 'Blazers' },
        { value: 'Sweaters', label: 'Sweaters' },
        { value: 'Hoodies', label: 'Hoodies' },
        { value: 'Shorts', label: 'Shorts' }
    ],

    // Kids + Indian Wear
    'Kids_Indianwear': [
        { value: 'All', label: 'All Kids Indian Wear' },
        { value: 'Boys Kurtas', label: 'Boys Kurtas' },
        { value: 'Girls Lehenga', label: 'Girls Lehenga' },
        { value: 'Kids Dhoti Sets', label: 'Kids Dhoti Sets' },
        { value: 'Girls Anarkali', label: 'Girls Anarkali' },
        { value: 'Boys Sherwanis', label: 'Boys Sherwanis' },
        { value: 'Girls Sharara', label: 'Girls Sharara' },
        { value: 'Kids Ethnic Sets', label: 'Kids Ethnic Sets' },
        { value: 'Traditional Frocks', label: 'Traditional Frocks' }
    ],

    // Kids + Western Wear
    'Kids_Westernwear': [
        { value: 'All', label: 'All Kids Western Wear' },
        { value: 'Boys Shirts', label: 'Boys Shirts' },
        { value: 'Girls Dresses', label: 'Girls Dresses' },
        { value: 'Boys T-Shirts', label: 'Boys T-Shirts' },
        { value: 'Girls Tops', label: 'Girls Tops' },
        { value: 'Boys Pants', label: 'Boys Pants' },
        { value: 'Girls Skirts', label: 'Girls Skirts' },
        { value: 'Boys Shorts', label: 'Boys Shorts' },
        { value: 'Girls Shorts', label: 'Girls Shorts' },
        { value: 'Kids Jeans', label: 'Kids Jeans' },
        { value: 'Kids Hoodies', label: 'Kids Hoodies' },
        { value: 'Kids Jackets', label: 'Kids Jackets' }
    ],

    // Unisex + Any Type
    'Unisex_Indianwear': [
        { value: 'All', label: 'All Unisex Indian Wear' },
        { value: 'Kurtas', label: 'Kurtas' },
        { value: 'Ethnic Wear', label: 'Ethnic Wear' },
        { value: 'Traditional Accessories', label: 'Traditional Accessories' }
    ],

    'Unisex_Westernwear': [
        { value: 'All', label: 'All Unisex Western Wear' },
        { value: 'T-Shirts', label: 'T-Shirts' },
        { value: 'Hoodies', label: 'Hoodies' },
        { value: 'Sweatshirts', label: 'Sweatshirts' },
        { value: 'Track Suits', label: 'Track Suits' }
    ],

    // Accessories (Gender Independent)
    'All_Accessories': [
        { value: 'All', label: 'All Accessories' },
        { value: 'Jewelry', label: 'Jewelry' },
        { value: 'Bags', label: 'Bags' },
        { value: 'Belts', label: 'Belts' },
        { value: 'Scarves', label: 'Scarves' },
        { value: 'Watches', label: 'Watches' },
        { value: 'Sunglasses', label: 'Sunglasses' },
        { value: 'Hair Accessories', label: 'Hair Accessories' },
        { value: 'Wallets', label: 'Wallets' }
    ],

    // Footwear (Gender Independent)
    'All_Footwear': [
        { value: 'All', label: 'All Footwear' },
        { value: 'Sandals', label: 'Sandals' },
        { value: 'Heels', label: 'Heels' },
        { value: 'Flats', label: 'Flats' },
        { value: 'Sneakers', label: 'Sneakers' },
        { value: 'Boots', label: 'Boots' },
        { value: 'Formal Shoes', label: 'Formal Shoes' },
        { value: 'Sports Shoes', label: 'Sports Shoes' },
        { value: 'Slippers', label: 'Slippers' }
    ],

    // Fusion (Gender Independent)
    'All_Fusion': [
        { value: 'All', label: 'All Fusion Wear' },
        { value: 'Indo Western', label: 'Indo Western' },
        { value: 'Fusion Dresses', label: 'Fusion Dresses' },
        { value: 'Contemporary Ethnic', label: 'Contemporary Ethnic' },
        { value: 'Modern Traditional', label: 'Modern Traditional' }
    ],

    // Default/Fallback
    'All_All': [
        { value: 'All', label: 'All Categories' },
        { value: 'Clothing', label: 'Clothing' },
        { value: 'Accessories', label: 'Accessories' },
        { value: 'Footwear', label: 'Footwear' }
    ]
};

// Function to get filtered categories based on gender and type
const getFilteredCategories = (gender, type) => {
    // Handle special cases for Accessories, Footwear, and Fusion
    if (type === 'Accessories') {
        return CATEGORY_MAPPING['All_Accessories'];
    }
    if (type === 'Footwear') {
        return CATEGORY_MAPPING['All_Footwear'];
    }
    if (type === 'Fusion') {
        return CATEGORY_MAPPING['All_Fusion'];
    }

    // Create key for mapping
    const key = `${gender}_${type}`;

    // Return specific mapping or fallback to default
    return CATEGORY_MAPPING[key] || CATEGORY_MAPPING['All_All'];
};

const GENDER_OPTIONS = [
    { value: 'All', label: 'All' },
    { value: 'Men', label: 'Men' },
    { value: 'Women', label: 'Women' },
    { value: 'Kids', label: 'Kids' },
    { value: 'Unisex', label: 'Unisex' }
];

const TYPE_OPTIONS = [
    { value: 'All', label: 'All Types' },
    { value: 'Indianwear', label: 'Indian Wear' },
    { value: 'Westernwear', label: 'Western Wear' },
    { value: 'Fusion', label: 'Fusion Wear' },
    { value: 'Accessories', label: 'Accessories' },
    { value: 'Footwear', label: 'Footwear' }
];

const STYLE_CATEGORY_OPTIONS = [
    { value: 'All', label: 'All Styles' },
    { value: 'Casual', label: 'Casual' },
    { value: 'Formal', label: 'Formal' },
    { value: 'Party', label: 'Party Wear' },
    { value: 'Traditional', label: 'Traditional' },
    { value: 'Contemporary', label: 'Contemporary' },
    { value: 'Vintage', label: 'Vintage' },
    { value: 'Bohemian', label: 'Bohemian' },
    { value: 'Minimalist', label: 'Minimalist' },
    { value: 'Luxury', label: 'Luxury' }
];

// Modern Input Component (moved outside to prevent re-creation)
const ModernInput = ({
    label,
    type = "text",
    value,
    onChange,
    placeholder,
    required = false,
    className = "",
    description = "",
    icon: Icon = null,
    validationRules = {},
    fieldPath = "",
    showValidation = true,
    validationErrors = {},
    setValidationErrors = () => {}
}) => {
    const error = validationErrors[fieldPath];
    const hasError = error && showValidation;

    const validateField = (path, value, rules = {}) => {
        const errors = [];
        if (rules.required && (!value || value.toString().trim() === '')) {
            errors.push('This field is required');
        }
        if (rules.email && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errors.push('Invalid email format');
        }
        if (rules.phone && value && !/^[\+]?[0-9\s\-\(\)]{10,}$/.test(value.replace(/\s/g, ''))) {
            errors.push('Invalid phone number');
        }
        if (rules.url && value && !/^https?:\/\/.+\..+/.test(value)) {
            errors.push('Invalid URL format');
        }
        if (rules.minLength && value && value.length < rules.minLength) {
            errors.push(`Minimum ${rules.minLength} characters required`);
        }
        if (rules.maxLength && value && value.length > rules.maxLength) {
            errors.push(`Maximum ${rules.maxLength} characters allowed`);
        }
        return errors.length > 0 ? errors[0] : null;
    };

    return (
        <div className={`space-y-2 ${className}`}>
            <label className="block text-sm font-semibold text-gray-800">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {description && <p className="text-xs text-gray-600">{description}</p>}
            <div className="relative">
                {Icon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Icon className={`h-5 w-5 ${hasError ? 'text-red-400' : 'text-gray-400'}`} />
                    </div>
                )}
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={(e) => {
                        // Validate on blur instead of on every change
                        if (fieldPath && validationRules && Object.keys(validationRules).length > 0) {
                            const error = validateField(fieldPath, e.target.value, validationRules);
                            if (error) {
                                setValidationErrors(prev => ({
                                    ...prev,
                                    [fieldPath]: error
                                }));
                            } else {
                                setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[fieldPath];
                                    return newErrors;
                                });
                            }
                        }
                    }}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${
                        Icon ? 'pl-10' : ''
                    } ${
                        hasError
                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                            : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                    }`}
                    placeholder={placeholder}
                />
                {hasError && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                )}
            </div>
            {hasError && (
                <div className="flex items-center space-x-1 text-red-600 text-xs">
                    <AlertCircle className="w-3 h-3" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

// Modern Textarea Component (moved outside to prevent re-creation)
const ModernTextarea = ({
    label,
    value,
    onChange,
    placeholder,
    required = false,
    rows = 4,
    className = "",
    description = "",
    validationRules = {},
    fieldPath = "",
    showValidation = true,
    validationErrors = {},
    setValidationErrors = () => {}
}) => {
    const error = validationErrors[fieldPath];
    const hasError = error && showValidation;

    const validateField = (path, value, rules = {}) => {
        const errors = [];
        if (rules.required && (!value || value.toString().trim() === '')) {
            errors.push('This field is required');
        }
        if (rules.minLength && value && value.length < rules.minLength) {
            errors.push(`Minimum ${rules.minLength} characters required`);
        }
        if (rules.maxLength && value && value.length > rules.maxLength) {
            errors.push(`Maximum ${rules.maxLength} characters allowed`);
        }
        return errors.length > 0 ? errors[0] : null;
    };

    return (
        <div className={`space-y-2 ${className}`}>
            <label className="block text-sm font-semibold text-gray-800">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {description && <p className="text-xs text-gray-600">{description}</p>}
            <div className="relative">
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={(e) => {
                        // Validate on blur instead of on every change
                        if (fieldPath && validationRules && Object.keys(validationRules).length > 0) {
                            const error = validateField(fieldPath, e.target.value, validationRules);
                            if (error) {
                                setValidationErrors(prev => ({
                                    ...prev,
                                    [fieldPath]: error
                                }));
                            } else {
                                setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[fieldPath];
                                    return newErrors;
                                });
                            }
                        }
                    }}
                    rows={rows}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 resize-none ${
                        hasError
                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                            : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                    }`}
                    placeholder={placeholder}
                />
                {hasError && (
                    <div className="absolute top-3 right-3 pointer-events-none">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                )}
            </div>
            {hasError && (
                <div className="flex items-center space-x-1 text-red-600 text-xs">
                    <AlertCircle className="w-3 h-3" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

// Modern Select Component (moved outside to prevent re-creation)
const ModernSelect = ({
    label,
    value,
    onChange,
    options,
    placeholder,
    required = false,
    className = "",
    validationRules = {},
    fieldPath = "",
    showValidation = true,
    description = "",
    validationErrors = {},
    setValidationErrors = () => {}
}) => {
    const error = validationErrors[fieldPath];
    const hasError = error && showValidation;

    const validateField = (path, value, rules = {}) => {
        const errors = [];
        if (rules.required && (!value || value.toString().trim() === '')) {
            errors.push('This field is required');
        }
        return errors.length > 0 ? errors[0] : null;
    };

    return (
        <div className={`space-y-2 ${className}`}>
            <label className="block text-sm font-semibold text-gray-800">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {description && <p className="text-xs text-gray-600">{description}</p>}
            <div className="relative">
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={(e) => {
                        // Validate on blur instead of on every change
                        if (fieldPath && validationRules && Object.keys(validationRules).length > 0) {
                            const error = validateField(fieldPath, e.target.value, validationRules);
                            if (error) {
                                setValidationErrors(prev => ({
                                    ...prev,
                                    [fieldPath]: error
                                }));
                            } else {
                                setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[fieldPath];
                                    return newErrors;
                                });
                            }
                        }
                    }}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-white ${
                        hasError
                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                            : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                    }`}
                >
                    {placeholder && <option value="">{placeholder}</option>}
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {hasError && (
                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                )}
            </div>
            {hasError && (
                <div className="flex items-center space-x-1 text-red-600 text-xs">
                    <AlertCircle className="w-3 h-3" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

const TemplateThemes = () => {
    const { authToken, user } = useContext(AuthContext);
    const navigate = useNavigate();

    // State management
    const [currentStep, setCurrentStep] = useState('loading');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [userThemes, setUserThemes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentSection, setCurrentSection] = useState('brand');
    const [userTemplateData, setUserTemplateData] = useState(null);
    const [hasExistingTemplate, setHasExistingTemplate] = useState(false);
    const [previewMode, setPreviewMode] = useState('desktop');
    const [showPreview, setShowPreview] = useState(false);

    // Enhanced state for better UX
    const [completedSections, setCompletedSections] = useState(new Set());
    const [validationErrors, setValidationErrors] = useState({});
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    // Define all possible sections with enhanced metadata
    const allSections = [
        {
            id: 'brand',
            name: 'Brand Identity',
            icon: Sparkles,
            description: 'Your brand essentials - logo, colors, and identity',
            category: 'Core',
            required: true
        },
        {
            id: 'hero',
            name: 'Hero Section',
            icon: Monitor,
            description: 'Stunning hero banners that capture attention',
            category: 'Layout',
            required: false
        },
        {
            id: 'categories',
            name: 'Categories',
            icon: Layers,
            description: 'Product categories with beautiful visuals',
            category: 'Content',
            required: false
        },
        {
            id: 'banners',
            name: 'Banners',
            icon: ImagePlus,
            description: 'Eye-catching promotional banners',
            category: 'Content',
            required: false
        },
        {
            id: 'sliding_banners',
            name: 'Sliding Banners',
            icon: RefreshCw,
            description: 'Dynamic carousel banners with smooth animations',
            category: 'Interactive',
            required: false
        },
        {
            id: 'promo_banners',
            name: 'Promo Banners',
            icon: Star,
            description: 'Special offers and promotional content',
            category: 'Marketing',
            required: false
        },
        {
            id: 'fashion_trends',
            name: 'Fashion Trends',
            icon: Palette,
            description: 'Showcase latest trends and seasonal collections',
            category: 'Content',
            required: false
        },
        {
            id: 'videos',
            name: 'Videos',
            icon: PlayCircle,
            description: 'Engaging video content and showcases',
            category: 'Media',
            required: false
        },
        {
            id: 'collections',
            name: 'Collections',
            icon: ShoppingBag,
            description: 'Featured product collections and highlights',
            category: 'Commerce',
            required: false
        },
        {
            id: 'statement',
            name: 'Brand Statement',
            icon: Edit,
            description: 'Powerful brand messaging and philosophy',
            category: 'Content',
            required: false
        },
        {
            id: 'workshop',
            name: 'Workshop',
            icon: Camera,
            description: 'Behind-the-scenes and craftsmanship stories',
            category: 'Content',
            required: false
        },
        {
            id: 'gallery',
            name: 'Gallery',
            icon: ImageIcon,
            description: 'Beautiful image galleries and portfolios',
            category: 'Visual',
            required: false
        },
        {
            id: 'instagram',
            name: 'Instagram Feed',
            icon: Heart,
            description: 'Social media integration and live feeds',
            category: 'Social',
            required: false
        },
        {
            id: 'craftsmanship',
            name: 'Craftsmanship',
            icon: Settings,
            description: 'Showcase your craft and expertise with statistics',
            category: 'Advanced',
            required: false
        },
        {
            id: 'designer_showcase',
            name: 'Designer Showcase',
            icon: Star,
            description: 'Feature your design team and their profiles',
            category: 'Advanced',
            required: false
        },
        {
            id: 'media_showcase',
            name: 'Media Showcase',
            icon: Monitor,
            description: 'Advanced media presentation with statistics',
            category: 'Advanced',
            required: false
        },
        {
            id: 'portfolio_showcase',
            name: 'Portfolio Showcase',
            icon: Layers,
            description: 'Featured projects and portfolio items',
            category: 'Advanced',
            required: false
        },
        {
            id: 'product_spotlight',
            name: 'Product Spotlight',
            icon: Zap,
            description: 'Highlight featured products with pricing',
            category: 'Advanced',
            required: false
        },
        {
            id: 'brand_story',
            name: 'Brand Story',
            icon: Heart,
            description: 'Tell your brand story with statistics and values',
            category: 'Advanced',
            required: false
        },
        {
            id: 'luxury_services',
            name: 'Luxury Services',
            icon: Star,
            description: 'Premium services and exclusive offerings',
            category: 'Advanced',
            required: false
        },
        {
            id: 'premium_collections',
            name: 'Premium Collections',
            icon: ShoppingBag,
            description: 'Luxury product collections showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'heritage',
            name: 'Heritage',
            icon: Settings,
            description: 'Brand heritage and timeline',
            category: 'Advanced',
            required: false
        },
        {
            id: 'events',
            name: 'Events',
            icon: Calendar,
            description: 'Upcoming events and experiences',
            category: 'Advanced',
            required: false
        },
        {
            id: 'human_values',
            name: 'Human Values',
            icon: Users,
            description: 'Team showcase and company values',
            category: 'Advanced',
            required: false
        },
        {
            id: 'environmental_impact',
            name: 'Environmental Impact',
            icon: Leaf,
            description: 'Sustainability and environmental initiatives',
            category: 'Advanced',
            required: false
        },
        {
            id: 'quality_assurance',
            name: 'Quality Assurance',
            icon: CheckCircle,
            description: 'Quality processes and guarantees',
            category: 'Advanced',
            required: false
        },
        {
            id: 'quality_craftsmanship',
            name: 'Quality & Craftsmanship',
            icon: Wrench,
            description: 'Quality features and craftsmanship details',
            category: 'Advanced',
            required: false
        },
        {
            id: 'featured_products',
            name: 'Featured Products',
            icon: Package,
            description: 'Highlighted product showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'featured_stories',
            name: 'Featured Stories',
            icon: BookOpen,
            description: 'Magazine-style editorial content',
            category: 'Advanced',
            required: false
        },
        {
            id: 'editors_picks',
            name: "Editor's Picks",
            icon: Sparkles,
            description: 'Curated product selections',
            category: 'Advanced',
            required: false
        },
        {
            id: 'quote_section',
            name: 'Quote Section',
            icon: Quote,
            description: 'Inspirational quotes and testimonials',
            category: 'Advanced',
            required: false
        },
        {
            id: 'portfolio_gallery',
            name: 'Portfolio Gallery',
            icon: ImageIcon,
            description: 'Creative portfolio showcase gallery',
            category: 'Advanced',
            required: false
        },
        {
            id: 'artist_statement',
            name: 'Artist Statement',
            icon: Edit,
            description: 'Artist biography and creative statement',
            category: 'Advanced',
            required: false
        },
        {
            id: 'studio_visit',
            name: 'Studio Visit',
            icon: MapPin,
            description: 'Studio location and visit information',
            category: 'Advanced',
            required: false
        },
        {
            id: 'luxury_statement',
            name: 'Luxury Statement',
            icon: Star,
            description: 'Heritage and luxury brand statement',
            category: 'Advanced',
            required: false
        },
        {
            id: 'video_story',
            name: 'Video Story',
            icon: Video,
            description: 'Brand story video section',
            category: 'Content',
            required: false
        },
        {
            id: 'newsletter',
            name: 'Newsletter',
            icon: Mail,
            description: 'Email newsletter signup section',
            category: 'Marketing',
            required: false
        },
        // Template 15 (Artisan) sections
        {
            id: 'collections_showcase',
            name: 'Collections Showcase',
            icon: Layers,
            description: 'Artisan collections with traditional craftsmanship',
            category: 'Advanced',
            required: false
        },
        {
            id: 'brand_values',
            name: 'Brand Values',
            icon: Heart,
            description: 'Core brand values and principles showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'video_section',
            name: 'Video Section',
            icon: PlayCircle,
            description: 'Brand video content with storytelling',
            category: 'Media',
            required: false
        },
        {
            id: 'heritage_statement',
            name: 'Heritage Statement',
            icon: Quote,
            description: 'Brand heritage and legacy statement',
            category: 'Advanced',
            required: false
        },
        {
            id: 'signature_collection',
            name: 'Signature Collection',
            icon: Star,
            description: 'Exclusive signature product collection',
            category: 'Advanced',
            required: false
        },
        // Template 16 (Heritage) sections
        {
            id: 'hero_collection',
            name: 'Hero Collection',
            icon: Star,
            description: 'Featured hero collection with story',
            category: 'Advanced',
            required: false
        },
        {
            id: 'artisan_story',
            name: 'Artisan Story',
            icon: Users,
            description: 'Master artisan profile and expertise',
            category: 'Advanced',
            required: false
        },
        {
            id: 'product_categories',
            name: 'Product Categories',
            icon: Grid,
            description: 'Organized product category display',
            category: 'Content',
            required: false
        },
        {
            id: 'brand_heritage',
            name: 'Brand Heritage',
            icon: Settings,
            description: 'Brand history and heritage timeline',
            category: 'Advanced',
            required: false
        },
        {
            id: 'newsletter_signup',
            name: 'Newsletter Signup',
            icon: Mail,
            description: 'Enhanced newsletter subscription section',
            category: 'Marketing',
            required: false
        },
        {
            id: 'featured_gallery',
            name: 'Featured Gallery',
            icon: ImageIcon,
            description: 'Curated featured image gallery',
            category: 'Visual',
            required: false
        },
        // Template 17 (Nordic) sections
        {
            id: 'featured_collection',
            name: 'Featured Collection',
            icon: Star,
            description: 'Seasonal featured collection showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'minimalist_showcase',
            name: 'Minimalist Showcase',
            icon: Layout,
            description: 'Clean minimalist product showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'seasonal_products',
            name: 'Seasonal Products',
            icon: Calendar,
            description: 'Season-specific product collections',
            category: 'Content',
            required: false
        },
        {
            id: 'design_philosophy',
            name: 'Design Philosophy',
            icon: Heart,
            description: 'Brand design principles and philosophy',
            category: 'Advanced',
            required: false
        },
        {
            id: 'sustainability_story',
            name: 'Sustainability Story',
            icon: Leaf,
            description: 'Environmental sustainability initiatives',
            category: 'Advanced',
            required: false
        },
        {
            id: 'product_grid',
            name: 'Product Grid',
            icon: Grid,
            description: 'Organized product grid display',
            category: 'Content',
            required: false
        },
        // Template 18 (Vintage) sections
        {
            id: 'vintage_collection',
            name: 'Vintage Collection',
            icon: Star,
            description: 'Classic vintage collection showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'retro_showcase',
            name: 'Retro Showcase',
            icon: Sparkles,
            description: 'Retro-inspired product showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'classic_products',
            name: 'Classic Products',
            icon: Package,
            description: 'Timeless classic product collection',
            category: 'Content',
            required: false
        },
        {
            id: 'nostalgia_story',
            name: 'Nostalgia Story',
            icon: Heart,
            description: 'Nostalgic brand storytelling section',
            category: 'Advanced',
            required: false
        },
        {
            id: 'timeless_gallery',
            name: 'Timeless Gallery',
            icon: ImageIcon,
            description: 'Vintage-inspired image gallery',
            category: 'Visual',
            required: false
        },
        {
            id: 'heritage_products',
            name: 'Heritage Products',
            icon: Star,
            description: 'Traditional heritage product showcase',
            category: 'Advanced',
            required: false
        },
        // Template 19 (Modern) sections
        {
            id: 'contemporary_showcase',
            name: 'Contemporary Showcase',
            icon: Zap,
            description: 'Modern contemporary design showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'innovation_hub',
            name: 'Innovation Hub',
            icon: Sparkles,
            description: 'Innovation and technology showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'modern_products',
            name: 'Modern Products',
            icon: Package,
            description: 'Contemporary modern product showcase',
            category: 'Content',
            required: false
        },
        {
            id: 'design_vision',
            name: 'Design Vision',
            icon: Eye,
            description: 'Future design vision and goals',
            category: 'Advanced',
            required: false
        },
        {
            id: 'future_trends',
            name: 'Future Trends',
            icon: ArrowRight,
            description: 'Upcoming trends and innovations',
            category: 'Advanced',
            required: false
        },
        {
            id: 'sleek_gallery',
            name: 'Sleek Gallery',
            icon: ImageIcon,
            description: 'Modern sleek image gallery',
            category: 'Visual',
            required: false
        },
        // Template 20 (Luxury) sections
        {
            id: 'premium_collection',
            name: 'Premium Collection',
            icon: Star,
            description: 'Ultra-luxury premium collection',
            category: 'Advanced',
            required: false
        },
        {
            id: 'exclusive_showcase',
            name: 'Exclusive Showcase',
            icon: Sparkles,
            description: 'Exclusive luxury product showcase',
            category: 'Advanced',
            required: false
        },
        {
            id: 'luxury_products',
            name: 'Luxury Products',
            icon: Package,
            description: 'Premium luxury product collection',
            category: 'Content',
            required: false
        },
        {
            id: 'craftsmanship_story',
            name: 'Craftsmanship Story',
            icon: Wrench,
            description: 'Master craftsmanship and artisanship',
            category: 'Advanced',
            required: false
        },
        {
            id: 'vip_experience',
            name: 'VIP Experience',
            icon: Star,
            description: 'Exclusive VIP services and experiences',
            category: 'Advanced',
            required: false
        },
        {
            id: 'elite_gallery',
            name: 'Elite Gallery',
            icon: ImageIcon,
            description: 'Elite luxury image gallery',
            category: 'Visual',
            required: false
        },
        // New sections for actual UI components
        {
            id: 'hero_with_values',
            name: 'Hero with Values',
            icon: Heart,
            description: 'Hero section with embedded brand values',
            category: 'Layout',
            required: false
        },
        {
            id: 'category_showcase',
            name: 'Category Showcase',
            icon: Grid,
            description: 'Category display with item counts',
            category: 'Content',
            required: false
        },
        {
            id: 'values_principles',
            name: 'Values & Principles',
            icon: Quote,
            description: 'Company values and core principles',
            category: 'Advanced',
            required: false
        },
        {
            id: 'category_grid',
            name: 'Category Grid',
            icon: Grid,
            description: 'Grid layout for product categories',
            category: 'Content',
            required: false
        },
        {
            id: 'quality_values',
            name: 'Quality Values',
            icon: CheckCircle,
            description: 'Quality-focused brand values',
            category: 'Advanced',
            required: false
        },
        {
            id: 'magazine_hero',
            name: 'Magazine Hero',
            icon: BookOpen,
            description: 'Magazine-style hero layout',
            category: 'Layout',
            required: false
        },
        {
            id: 'editorial_stories',
            name: 'Editorial Stories',
            icon: Edit,
            description: 'Magazine-style editorial articles',
            category: 'Content',
            required: false
        },
        {
            id: 'inspirational_quote',
            name: 'Inspirational Quote',
            icon: Quote,
            description: 'Large inspirational quote section',
            category: 'Content',
            required: false
        },
        {
            id: 'artist_hero',
            name: 'Artist Hero',
            icon: Palette,
            description: 'Artist-focused hero section',
            category: 'Layout',
            required: false
        },
        {
            id: 'studio_contact',
            name: 'Studio Contact',
            icon: MapPin,
            description: 'Studio visit and contact information',
            category: 'Core',
            required: false
        },
        {
            id: 'business',
            name: 'Contact Info',
            icon: FileText,
            description: 'Business contact details and information',
            category: 'Core',
            required: true
        }
    ];

    // Enhanced template definitions with modern design
    const templates = [
        {
            id: 1,
            name: 'Minimalist',
            image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop',
            description: 'Clean, timeless design focused on your products.',
            sections: ['brand', 'business'],
            color: 'from-gray-600 to-gray-800',
            features: ['Clean Design', 'Fast Loading', 'Mobile First']
        },
        {
            id: 2,
            name: 'Modern',
            image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
            description: 'Contemporary design with dynamic categories.',
            sections: ['brand', 'categories', 'business'],
            color: 'from-blue-600 to-purple-600',
            features: ['Category Focus', 'Modern UI', 'Responsive']
        },
        {
            id: 3,
            name: 'Banner Pro',
            image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop',
            description: 'Eye-catching banners to highlight your brand.',
            sections: ['brand', 'categories', 'banners', 'business'],
            color: 'from-orange-500 to-red-500',
            features: ['Banner Focus', 'Visual Impact', 'Marketing Ready']
        },
        {
            id: 4,
            name: 'Promo Central',
            image: 'https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?w=800&h=600&fit=crop',
            description: 'Comprehensive promotional sections with videos.',
            sections: ['brand', 'categories', 'hero', 'sliding_banners', 'promo_banners', 'fashion_trends', 'videos', 'business'],
            color: 'from-pink-500 to-rose-500',
            features: ['Video Integration', 'Promo Focus', 'Rich Content']
        },
        {
            id: 5,
            name: 'Dynamic Pro',
            image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=600&fit=crop',
            description: 'Premium layout with all advanced features.',
            sections: ['brand', 'categories', 'hero', 'sliding_banners', 'promo_banners', 'fashion_trends', 'videos', 'business'],
            color: 'from-purple-600 to-indigo-600',
            features: ['All Features', 'Premium Design', 'Maximum Flexibility']
        },
        {
            id: 6,
            name: 'Elegant',
            image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=600&fit=crop',
            description: 'Sophisticated design for luxury brands.',
            sections: ['brand', 'categories', 'banners', 'business'],
            color: 'from-emerald-500 to-teal-600',
            features: ['Luxury Focus', 'Elegant Design', 'Premium Feel']
        },
        {
            id: 7,
            name: 'Vogue',
            image: 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=800&h=600&fit=crop',
            description: 'High-fashion focused template design.',
            sections: ['brand', 'categories', 'banners', 'business'],
            color: 'from-rose-500 to-pink-600',
            features: ['Fashion Focus', 'Editorial Style', 'Trendy Design']
        },
        {
            id: 8,
            name: 'Vintage',
            image: 'https://images.unsplash.com/photo-1506629905649-53d5c19eb2f0?w=800&h=600&fit=crop',
            description: 'Retro-inspired design with classic elements.',
            sections: ['brand', 'categories', 'hero', 'sliding_banners', 'promo_banners', 'fashion_trends', 'videos', 'business'],
            color: 'from-amber-600 to-orange-600',
            features: ['Retro Style', 'Classic Feel', 'Nostalgic Design']
        },
        {
            id: 9,
            name: 'Artistic',
            image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop',
            description: 'Creative template for artistic brands.',
            sections: ['brand', 'categories', 'hero', 'sliding_banners', 'promo_banners', 'fashion_trends', 'videos', 'business'],
            color: 'from-cyan-500 to-blue-600',
            features: ['Creative Focus', 'Artistic Design', 'Unique Layout']
        },
        {
            id: 10,
            name: 'Dawn',
            image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=600&fit=crop',
            description: 'Fresh, modern design with clean hero sections.',
            sections: ['brand', 'hero', 'categories', 'business'],
            color: 'from-slate-500 to-gray-600',
            features: ['Clean Hero', 'Modern Layout', 'Fresh Design']
        },
        {
            id: 11,
            name: 'Prestige',
            image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop',
            description: 'Luxury dark theme with elegant collections.',
            sections: ['brand', 'hero', 'categories', 'collections', 'statement', 'business'],
            color: 'from-slate-800 to-gray-900',
            features: ['Dark Theme', 'Luxury Feel', 'Premium Collections']
        },
        {
            id: 12,
            name: 'Impact',
            image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=800&h=600&fit=crop',
            description: 'Bold typography and vibrant colors.',
            sections: ['brand', 'hero', 'categories', 'collections', 'statement', 'business'],
            color: 'from-violet-600 to-purple-700',
            features: ['Bold Typography', 'Vibrant Colors', 'Strong Impact']
        },
        {
            id: 13,
            name: 'Craft',
            image: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=800&h=600&fit=crop',
            description: 'Warm, artisan-focused design.',
            sections: ['brand', 'hero', 'categories', 'collections', 'workshop', 'business'],
            color: 'from-amber-700 to-orange-800',
            features: ['Artisan Focus', 'Warm Design', 'Craft Theme']
        },
        {
            id: 14,
            name: 'Studio',
            image: 'https://images.unsplash.com/photo-1493863641943-9b68992a8d07?w=800&h=600&fit=crop',
            description: 'Visual-first photography layout.',
            sections: ['brand', 'hero', 'categories', 'gallery', 'statement', 'instagram', 'business'],
            color: 'from-neutral-700 to-stone-800',
            features: ['Photography Focus', 'Visual First', 'Gallery Focused']
        },
        {
            id: 15,
            name: 'Minimalist',
            image: 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=800&h=600&fit=crop',
            description: 'Ultra-clean minimalist design focused on simplicity and quality with "Less is everything" philosophy.',
            sections: ['brand', 'hero', 'collections_showcase', 'brand_values', 'video_section', 'newsletter', 'business'],
            color: 'from-gray-400 to-slate-600',
            features: ['Minimalist Design', 'Quality Focus', 'Clean Aesthetics']
        },
        {
            id: 16,
            name: 'Prestige',
            image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop',
            description: 'Luxury prestige template with "Timeless ELEGANCE" and premium heritage services.',
            sections: ['brand', 'hero', 'heritage_statement', 'premium_services', 'business'],
            color: 'from-slate-600 to-gray-800',
            features: ['Luxury Services', 'Premium Experience', 'Heritage Focus']
        },
        {
            id: 17,
            name: 'Nordic',
            image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&h=600&fit=crop',
            description: 'Scandinavian-inspired sustainable design with "Natural beauty, thoughtful design" and Nordic family values.',
            sections: ['brand', 'hero_with_values', 'category_showcase', 'craftsmanship_story', 'video_story', 'values_principles', 'newsletter', 'business'],
            color: 'from-blue-400 to-cyan-600',
            features: ['Sustainable Design', 'Craftsmanship Focus', 'Environmental Impact']
        },
        {
            id: 18,
            name: 'Focal',
            image: 'https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=800&h=600&fit=crop',
            description: 'Photography-focused template with "Focus on what matters" clean design and quality emphasis.',
            sections: ['brand', 'hero', 'category_grid', 'quality_values', 'newsletter', 'business'],
            color: 'from-indigo-500 to-purple-700',
            features: ['Quality Focus', 'Clean Design', 'Product Showcase']
        },
        {
            id: 19,
            name: 'Broadcast',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=600&fit=crop',
            description: 'Editorial fashion template with magazine-style "The Future of Fashion" content and editorial picks.',
            sections: ['brand', 'magazine_hero', 'editorial_stories', 'inspirational_quote', 'newsletter', 'business'],
            color: 'from-orange-500 to-red-600',
            features: ['Editorial Design', 'Featured Stories', 'Curated Content']
        },
        {
            id: 20,
            name: 'Studio',
            image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop',
            description: 'Creative studio template with "Creative Expression" artist showcase and portfolio gallery.',
            sections: ['brand', 'artist_hero', 'portfolio_gallery', 'artist_statement', 'collections_showcase', 'studio_contact', 'business'],
            color: 'from-amber-500 to-orange-700',
            features: ['Portfolio Gallery', 'Artist Showcase', 'Studio Experience']
        }
    ];

    // Get sections for the selected template
    const getTemplateSections = () => {
        if (!selectedTemplate) return [];
        return allSections.filter(section =>
            selectedTemplate.sections.includes(section.id)
        );
    };

    // Enhanced form data structure
    const [formData, setFormData] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [previewImages, setPreviewImages] = useState({});

    // Axios instance with auth
    const authAxios = useMemo(() => {
        const instance = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL || 'https://vasstrinfo.com/api',
            headers: { Authorization: `Bearer ${authToken?.access || ''}` },
            withCredentials: true,
        });

        // Request interceptor for CSRF tokens
        instance.interceptors.request.use(config => {
            if (['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
                const csrfToken = Cookies.get('csrftoken');
                if (csrfToken) config.headers['X-CSRFToken'] = csrfToken;
            }
            return config;
        });

        // Response interceptor for authentication errors
        instance.interceptors.response.use(
            response => response,
            error => {
                if (error.response?.status === 401) {
                    console.error('Authentication failed. Token may be expired.');
                    Swal.fire({
                        title: 'Authentication Error',
                        text: 'Your session has expired. Please login again.',
                        icon: 'error',
                        confirmButtonText: 'Login'
                    }).then(() => {
                        // Clear auth and redirect to login
                        localStorage.removeItem('authToken');
                        localStorage.removeItem('access_token');
                        window.location.href = '/login';
                    });
                }
                return Promise.reject(error);
            }
        );

        return instance;
    }, [authToken?.access]);

    // Check user template status on mount
    useEffect(() => {
        const checkUserTemplateStatus = async () => {
            try {
                setIsLoading(true);
                const response = await authAxios.get('/template-themes/check-status/');

                if (response.data.success) {
                    if (response.data.data.has_template) {
                        setHasExistingTemplate(true);
                        setUserTemplateData(response.data.data);
                        setCurrentStep('view_data');
                    } else {
                        setHasExistingTemplate(false);
                        setCurrentStep('select');
                    }
                }
            } catch (error) {
                console.error('Error checking user template status:', error);
                setCurrentStep('select');
            } finally {
                setIsLoading(false);
            }
        };

        if (authToken?.access) {

            checkUserTemplateStatus();
        }
    }, [authToken, authAxios]);

    // Ensure all array items have stable IDs
    const ensureArrayItemIds = (data) => {
        const updatedData = { ...data };

        if (updatedData.content) {
            ['categories', 'videos', 'banners', 'sliding_banners', 'promo_banners', 'fashion_trends', 'collections', 'gallery', 'instagram'].forEach(sectionId => {
                if (Array.isArray(updatedData.content[sectionId])) {
                    updatedData.content[sectionId] = updatedData.content[sectionId].map((item, index) => ({
                        ...item,
                        id: item.id || `${sectionId}_${index}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                    }));
                }
            });
        }

        return updatedData;
    };

    // Initialize form data for template
    const initializeFormData = (template) => {
        const initialData = {
            template_id: `template_${template.id}_${Date.now()}`,
            theme: {
                colors: {
                    primary: '#000000',
                    secondary: '#ffffff',
                    accent: '#f3f4f6',
                    background: '#ffffff',
                    text: '#111827'
                },
                fonts: {
                    heading: 'Playfair Display',
                    body: 'Inter',
                    accent: 'IM Fell English'
                },
                spacing: 'comfortable',
                borderRadius: 'lg',
                shadows: 'soft'
            },
            layout: {
                sections: template.sections,
                heroStyle: 'overlay',
                categoryStyle: 'grid',
                showcaseStyle: 'grid',
                productLayout: 'standard_grid'
            },
            animations: {
                enabled: true,
                style: 'smooth',
                pageTransitions: true,
                hoverEffects: 'scale',
                scrollAnimations: 'enabled'
            },
            content: {}
        };

        // Initialize content sections
        template.sections.forEach(sectionId => {
            if (sectionId === 'brand') {
                initialData.content.brand = {
                    name: '',
                    tagline: '',
                    description: '',
                    logo: '',
                    owner: {
                        name: '',
                        bio: '',
                        image: '',
                        social: {
                            instagram: '',
                            facebook: '',
                            twitter: '',
                            whatsapp: '',
                            youtube: ''
                        }
                    }
                };
            } else if (sectionId === 'business') {
                initialData.content.business = {
                    contact: {
                        business_name: '',
                        phone: '',
                        email: '',
                        address: '',
                        working_hours: 'Mon-Sat: 10AM-8PM'
                    }
                };
            } else if (['categories', 'videos', 'banners', 'sliding_banners', 'promo_banners', 'fashion_trends', 'collections', 'gallery', 'instagram'].includes(sectionId)) {
                initialData.content[sectionId] = [];
            } else if (sectionId === 'hero') {
                initialData.content.hero = {
                    title: '',
                    subtitle: '',
                    description: '',
                    background_image: '',
                    cta_text: 'Shop Collection',
                    cta_link: '#products'
                };
            } else if (sectionId === 'statement') {
                initialData.content.statement = {
                    title: '',
                    subtitle: '',
                    background_image: ''
                };
            } else if (sectionId === 'workshop') {
                initialData.content.workshop = {
                    title: '',
                    description: '',
                    location: '',
                    image: ''
                };
            }
        });

        return initialData;
    };

    // Handle template selection
    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setCurrentStep('configure');
        setCurrentSection('brand');
        const initialData = initializeFormData(template);
        setFormData(ensureArrayItemIds(initialData));
        setUploadedFiles({});
        setPreviewImages({});
        setCompletedSections(new Set());
        setValidationErrors({});
    };

    // Function to populate form with existing template data
    const populateFormWithExistingData = () => {
        if (!userTemplateData || !userTemplateData.config_data) {
            console.log('No existing template data to populate');
            return;
        }

        const existingConfig = userTemplateData.config_data;
        const templateInfo = userTemplateData.template_info;

        console.log('🔄 Populating form with existing data:', existingConfig);

        // Set the selected template based on the existing template number
        const existingTemplate = templates.find(t => t.id === templateInfo.temp_no);
        if (existingTemplate) {
            setSelectedTemplate(existingTemplate);
            console.log('✅ Found and set existing template:', existingTemplate);
        }

        // Populate form data with existing configuration
        if (existingConfig) {
            setFormData(ensureArrayItemIds(existingConfig));
            console.log('✅ Form data populated from existing config');
        }

        // Set preview images for existing files
        const newPreviewImages = {};
        if (existingConfig.content) {
            // Handle brand logo
            if (existingConfig.content.brand?.logo) {
                newPreviewImages.logo = existingConfig.content.brand.logo;
            }

            // Handle hero image
            if (existingConfig.content.hero?.background_image) {
                newPreviewImages.hero_background = existingConfig.content.hero.background_image;
            }

            // Handle category images
            if (existingConfig.content.categories) {
                existingConfig.content.categories.forEach((category, index) => {
                    if (category.image) {
                        newPreviewImages[`category_image_${index}`] = category.image;
                    }
                });
            }

            // Handle banner images
            if (existingConfig.content.banners) {
                existingConfig.content.banners.forEach((banner, index) => {
                    if (banner.image) {
                        newPreviewImages[`banner_image_${index}`] = banner.image;
                    }
                });
            }

            // Handle sliding banner images
            if (existingConfig.content.sliding_banners) {
                existingConfig.content.sliding_banners.forEach((banner, index) => {
                    if (banner.image) {
                        newPreviewImages[`sliding_banner_image_${index}`] = banner.image;
                    }
                });
            }

            // Handle promo banner images
            if (existingConfig.content.promo_banners) {
                existingConfig.content.promo_banners.forEach((banner, index) => {
                    if (banner.image) {
                        newPreviewImages[`promo_banner_image_${index}`] = banner.image;
                    }
                });
            }

            // Handle fashion trend images
            if (existingConfig.content.fashion_trends) {
                existingConfig.content.fashion_trends.forEach((trend, index) => {
                    if (trend.image) {
                        newPreviewImages[`fashion_trend_image_${index}`] = trend.image;
                    }
                });
            }

            // Handle collection images
            if (existingConfig.content.collections) {
                existingConfig.content.collections.forEach((collection, index) => {
                    if (collection.image) {
                        newPreviewImages[`collection_image_${index}`] = collection.image;
                    }
                });
            }

            // Handle gallery images
            if (existingConfig.content.gallery) {
                existingConfig.content.gallery.forEach((item, index) => {
                    if (item.image) {
                        newPreviewImages[`gallery_image_${index + 1}`] = item.image;
                    }
                });
            }

            // Handle instagram images
            if (existingConfig.content.instagram) {
                existingConfig.content.instagram.forEach((item, index) => {
                    if (item.image) {
                        newPreviewImages[`instagram_image_${index + 1}`] = item.image;
                    }
                });
            }

            // Handle statement background image
            if (existingConfig.content.statement?.background_image) {
                newPreviewImages.statement_background_image = existingConfig.content.statement.background_image;
            }

            // Handle workshop image
            if (existingConfig.content.workshop?.image) {
                newPreviewImages.workshop_image = existingConfig.content.workshop.image;
            }
        }

        setPreviewImages(newPreviewImages);
        console.log('✅ Preview images set:', newPreviewImages);

        // Mark sections as completed based on existing data
        const completedSections = new Set();
        if (existingConfig.content) {
            Object.keys(existingConfig.content).forEach(sectionKey => {
                const sectionData = existingConfig.content[sectionKey];
                if (sectionData && Object.keys(sectionData).length > 0) {
                    completedSections.add(sectionKey);
                }
            });
        }
        setCompletedSections(completedSections);
        console.log('✅ Completed sections set:', Array.from(completedSections));
    };

    // Enhanced validation functions
    const validateField = (path, value, rules = {}) => {
        const errors = [];

        // Required validation
        if (rules.required && (!value || value.toString().trim() === '')) {
            errors.push('This field is required');
        }

        // Email validation
        if (rules.email && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                errors.push('Please enter a valid email address');
            }
        }

        // Phone validation
        if (rules.phone && value) {
            const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
            if (!phoneRegex.test(value.replace(/[\s\-\(\)]/g, ''))) {
                errors.push('Please enter a valid phone number');
            }
        }

        // URL validation
        if (rules.url && value) {
            try {
                new URL(value);
            } catch {
                errors.push('Please enter a valid URL');
            }
        }

        // Min length validation
        if (rules.minLength && value && value.length < rules.minLength) {
            errors.push(`Must be at least ${rules.minLength} characters long`);
        }

        // Max length validation
        if (rules.maxLength && value && value.length > rules.maxLength) {
            errors.push(`Must be no more than ${rules.maxLength} characters long`);
        }

        return errors;
    };

    // Enhanced input change handler with validation
    const handleInputChange = (path, value) => {
        console.log('🔄 handleInputChange called:', { path, value });
        setFormData(prev => {
            const newData = { ...prev };
            const keys = path.split('.');
            let current = newData;

            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                // Handle array indices (numbers)
                if (!isNaN(key)) {
                    // This is an array index
                    if (!Array.isArray(current)) {
                        current = [];
                    }
                    if (!current[parseInt(key)]) {
                        current[parseInt(key)] = {};
                    }
                    current = current[parseInt(key)];
                } else {
                    // This is an object key
                    if (!current[key]) {
                        // Check if the next key is a number (array index)
                        const nextKey = keys[i + 1];
                        current[key] = !isNaN(nextKey) ? [] : {};
                    }
                    current = current[key];
                }
            }

            const finalKey = keys[keys.length - 1];
            current[finalKey] = value;

            console.log('📝 Updated formData:', newData);
            return newData;
        });

        // Clear any existing validation errors for this field when user starts typing
        setValidationErrors(prev => {
            if (prev[path]) {
                const newErrors = { ...prev };
                delete newErrors[path];
                return newErrors;
            }
            return prev;
        });
    };

    // Section validation
    const validateSection = (sectionId) => {
        const errors = {};
        const sectionData = allSections.find(s => s.id === sectionId);

        if (sectionId === 'brand') {
            if (!formData.content?.brand?.name) {
                errors['content.brand.name'] = 'Brand name is required';
            }
            if (!formData.content?.brand?.description) {
                errors['content.brand.description'] = 'Brand description is required';
            }
            if (!uploadedFiles.brand_logo) {
                errors['brand_logo'] = 'Brand logo is required';
            }
        }

        if (sectionId === 'hero') {
            if (!formData.content?.hero?.title) {
                errors['content.hero.title'] = 'Hero title is required';
            }
            if (!uploadedFiles.hero_background) {
                errors['hero_background'] = 'Hero background image is required';
            }
        }

        if (sectionId === 'business') {
            if (!formData.content?.business?.contact?.business_name) {
                errors['content.business.contact.business_name'] = 'Business name is required';
            }
            if (!formData.content?.business?.contact?.email) {
                errors['content.business.contact.email'] = 'Email address is required';
            }
            if (!formData.content?.business?.contact?.phone) {
                errors['content.business.contact.phone'] = 'Phone number is required';
            }
        }

        return errors;
    };

    // Form completion checker
    const isSectionComplete = (sectionId) => {
        const errors = validateSection(sectionId);
        return Object.keys(errors).length === 0;
    };

    // Enhanced array item management with drag-and-drop
    const addArrayItem = useCallback((sectionId, itemTemplate = {}) => {
        setFormData(prev => ({
            ...prev,
            content: {
                ...prev.content,
                [sectionId]: [
                    ...(prev.content[sectionId] || []),
                    {
                        ...itemTemplate,
                        index: (prev.content[sectionId]?.length || 0) + 1,
                        id: `${sectionId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                    }
                ]
            }
        }));
    }, []);

    const removeArrayItem = useCallback((sectionId, index) => {
        setFormData(prev => ({
            ...prev,
            content: {
                ...prev.content,
                [sectionId]: prev.content[sectionId].filter((_, i) => i !== index)
            }
        }));
    }, []);

    const updateArrayItem = useCallback((sectionId, index, field, value) => {
        setFormData(prev => ({
            ...prev,
            content: {
                ...prev.content,
                [sectionId]: prev.content[sectionId].map((item, i) =>
                    i === index ? {
                        ...item,
                        [field]: value,
                        // Ensure item has a stable ID
                        id: item.id || `${sectionId}_${index}_${Date.now()}`
                    } : item
                )
            }
        }));
    }, []);

    // Drag and drop functionality
    const moveArrayItem = (sectionId, fromIndex, toIndex) => {
        setFormData(prev => {
            const items = [...(prev.content[sectionId] || [])];
            const [movedItem] = items.splice(fromIndex, 1);
            items.splice(toIndex, 0, movedItem);

            // Update indices
            const updatedItems = items.map((item, index) => ({
                ...item,
                index: index + 1
            }));

            return {
                ...prev,
                content: {
                    ...prev.content,
                    [sectionId]: updatedItems
                }
            };
        });
    };

    // Enhanced file upload handler
    const handleFileUpload = (fieldName, file) => {
        if (file) {
            setUploadedFiles(prev => ({
                ...prev,
                [fieldName]: file
            }));

            if (file.type.startsWith('image/')) {
                const previewUrl = URL.createObjectURL(file);
                setPreviewImages(prev => ({
                    ...prev,
                    [fieldName]: previewUrl
                }));
            }
        }
    };

    // Modern file upload component
    const FileUploadArea = ({
        fieldName,
        label,
        accept = "image/*",
        required = false,
        dimensions = "",
        description = "",
        className = ""
    }) => (
        <div className={`space-y-3 ${className}`}>
            <label className="block text-sm font-semibold text-gray-800">
                {label} {required && <span className="text-red-500">*</span>}
                {dimensions && <span className="text-xs font-normal text-gray-500 ml-2">({dimensions})</span>}
            </label>
            {description && <p className="text-xs text-gray-600">{description}</p>}

            <div className="relative">
                <input
                    type="file"
                    accept={accept}
                    onChange={(e) => handleFileUpload(fieldName, e.target.files[0])}
                    className="hidden"
                    id={fieldName}
                />
                <label
                    htmlFor={fieldName}
                    className="block border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 cursor-pointer group"
                >
                    <div className="text-center">
                        {accept.includes('video') ? (
                            <PlayCircle className="w-12 h-12 mx-auto text-gray-400 group-hover:text-blue-500 transition-colors mb-3" />
                        ) : (
                            <ImagePlus className="w-12 h-12 mx-auto text-gray-400 group-hover:text-blue-500 transition-colors mb-3" />
                        )}
                        <p className="text-sm font-medium text-gray-700 group-hover:text-blue-600">
                            Click to upload {label.toLowerCase()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            {accept.includes('video')
                                ? 'MP4, WebM, OGG up to 50MB'
                                : 'PNG, JPG, WebP up to 10MB'}
                        </p>
                    </div>
                </label>

                {uploadedFiles[fieldName] && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-green-800">{uploadedFiles[fieldName].name}</p>
                                <p className="text-xs text-green-600">Upload successful</p>
                            </div>
                        </div>
                        {previewImages[fieldName] && (
                            <div className="mt-3">
                                <img
                                    src={previewImages[fieldName]}
                                    alt="Preview"
                                    className="max-w-full h-32 object-cover rounded-lg shadow-sm"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );


    // Enhanced file upload component with preview
    const ModernFileUpload = ({
        label,
        value,
        onChange,
        placeholder = "Click to upload or drag and drop",
        required = false,
        className = "",
        description = "",
        accept = "image/*",
        fileType = "image", // 'image' or 'video'
        fieldPath = "",
        validationRules = {},
        showValidation = true,
        dimensions = ""
    }) => {
        const [isDragOver, setIsDragOver] = useState(false);
        const fileInputRef = useRef(null);
        const error = validationErrors[fieldPath];
        const hasError = error && showValidation;

        // Use centralized preview state instead of local state
        const preview = previewImages[fieldPath] || '';

        const handleFileSelect = (file) => {
            if (file) {
                // Store the actual file object for upload
                setUploadedFiles(prev => ({
                    ...prev,
                    [fieldPath]: file
                }));

                // Create preview for display
                const reader = new FileReader();
                reader.onload = (e) => {
                    const result = e.target.result;

                    // Store preview for display
                    setPreviewImages(prev => ({
                        ...prev,
                        [fieldPath]: result
                    }));

                    // Store file name/path in form data for form validation
                    onChange(file.name);

                    // Validate the uploaded file
                    if (fieldPath && validationRules && Object.keys(validationRules).length > 0) {
                        const error = validateField(fieldPath, file.name, validationRules);
                        if (error) {
                            setValidationErrors(prev => ({
                                ...prev,
                                [fieldPath]: error
                            }));
                        } else {
                            setValidationErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[fieldPath];
                                return newErrors;
                            });
                        }
                    }
                };
                reader.readAsDataURL(file);
            }
        };

        const handleDrop = (e) => {
            e.preventDefault();
            setIsDragOver(false);
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                handleFileSelect(files[0]);
            }
        };

        const handleDragOver = (e) => {
            e.preventDefault();
            setIsDragOver(true);
        };

        const handleDragLeave = (e) => {
            e.preventDefault();
            setIsDragOver(false);
        };

        const handleClick = () => {
            fileInputRef.current?.click();
        };

        const removeFile = () => {
            onChange('');

            // Clear from uploadedFiles state
            setUploadedFiles(prev => {
                const newFiles = { ...prev };
                delete newFiles[fieldPath];
                return newFiles;
            });

            // Clear from preview images
            setPreviewImages(prev => {
                const newPreviews = { ...prev };
                delete newPreviews[fieldPath];
                return newPreviews;
            });

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Clear validation errors for this field
            if (fieldPath) {
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldPath];
                    return newErrors;
                });
            }
        };

        return (
            <div className={`space-y-2 ${className}`}>
                <label className="block text-sm font-semibold text-gray-800">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
                {description && <p className="text-xs text-gray-600">{description}</p>}
                {dimensions && <p className="text-xs text-blue-600">Recommended: {dimensions}</p>}

                <div
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 cursor-pointer ${
                        isDragOver
                            ? 'border-blue-400 bg-blue-50'
                            : hasError
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={handleClick}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={accept}
                        onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                                handleFileSelect(e.target.files[0]);
                            }
                        }}
                        className="hidden"
                    />

                    {preview ? (
                        <div className="relative">
                            {fileType === 'image' ? (
                                <img
                                    src={preview}
                                    alt="Preview"
                                    className="max-w-full max-h-48 mx-auto rounded-lg shadow-sm"
                                />
                            ) : (
                                <video
                                    src={preview}
                                    controls
                                    className="max-w-full max-h-48 mx-auto rounded-lg shadow-sm"
                                />
                            )}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeFile();
                                }}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {fileType === 'image' ? (
                                <ImageIcon className="w-12 h-12 mx-auto text-gray-400" />
                            ) : (
                                <PlayCircle className="w-12 h-12 mx-auto text-gray-400" />
                            )}
                            <p className="text-gray-600">{placeholder}</p>
                            <p className="text-xs text-gray-500">
                                {fileType === 'image' ? 'PNG, JPG, GIF up to 10MB' : 'MP4, MOV, AVI up to 50MB'}
                            </p>
                        </div>
                    )}
                </div>

                {hasError && (
                    <div className="flex items-center space-x-1 text-red-600 text-xs">
                        <AlertCircle className="w-3 h-3" />
                        <span>{error}</span>
                    </div>
                )}
            </div>
        );
    };

    // Enhanced item manager for arrays (supports up to 5 items with drag-and-drop)
    const ItemManager = ({
        sectionId,
        items = [],
        itemTemplate,
        renderItem,
        title,
        description,
        maxItems = 5
    }) => {
        const [draggedIndex, setDraggedIndex] = useState(null);
        const [dragOverIndex, setDragOverIndex] = useState(null);

        // Handle drag start
        const handleDragStart = (e, index) => {
            setDraggedIndex(index);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/html', e.target.outerHTML);
        };

        // Handle drag over
        const handleDragOver = (e, index) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';

            if (index !== draggedIndex) {
                setDragOverIndex(index);
            }
        };

        // Handle drag end
        const handleDragEnd = () => {
            setDraggedIndex(null);
            setDragOverIndex(null);
        };

        // Handle drop
        const handleDrop = (e, dropIndex) => {
            e.preventDefault();

            if (draggedIndex !== null && draggedIndex !== dropIndex) {
                moveArrayItem(sectionId, draggedIndex, dropIndex);
            }

            setDraggedIndex(null);
            setDragOverIndex(null);
        };

        const getDragStyles = (index) => {
            let styles = "bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 cursor-move relative";

            if (draggedIndex === index) {
                styles += " opacity-50 transform rotate-2 scale-105 shadow-lg";
            }

            if (dragOverIndex === index && draggedIndex !== index) {
                styles += " border-blue-400 bg-blue-50 shadow-lg transform scale-102";
            }

            return styles;
        };

        return (
            <div className="space-y-6">
                <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
                    <p className="text-gray-600">{description}</p>
                    <div className="mt-2 text-sm text-blue-600">
                        {items.length} of {maxItems} items added
                    </div>
                    {items.length > 1 && (
                        <div className="mt-2 text-xs text-gray-500 flex items-center justify-center">
                            <Move className="w-3 h-3 mr-1" />
                            Drag items to reorder
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    {items.map((item, index) => (
                        <div
                            key={item.id || index}
                            draggable={items.length > 1}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            onDrop={(e) => handleDrop(e, index)}
                            className={getDragStyles(index)}
                        >
                            {/* Drop zone indicator */}
                            {dragOverIndex === index && draggedIndex !== index && (
                                <div className="absolute -top-2 left-0 right-0 h-1 bg-blue-500 rounded-full animate-pulse"></div>
                            )}

                            <div className="flex justify-between items-center mb-4">
                                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                                        {index + 1}
                                    </span>
                                    {title} {index + 1}
                                </h4>
                                <div className="flex space-x-2 items-center">
                                    {items.length > 1 && (
                                        <>
                                            <button
                                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-grab active:cursor-grabbing"
                                                title="Drag to reorder"
                                            >
                                                <Move className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => removeArrayItem(sectionId, index)}
                                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                                                title="Delete item"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {renderItem(item, index)}
                        </div>
                    ))}
                </div>

                {items.length < maxItems && (
                    <button
                        onClick={() => addArrayItem(sectionId, itemTemplate)}
                        className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 group"
                    >
                        <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                        <span className="text-blue-600 group-hover:text-blue-700 font-medium">
                            Add {title} ({items.length + 1}/{maxItems})
                        </span>
                    </button>
                )}
            </div>
        );
    };

    // Real-time preview component
    const PreviewModal = () => {
        if (!showPreviewModal) return null;

        const getPreviewContent = () => {
            const brandData = formData.content?.brand || {};
            const heroData = formData.content?.hero || {};
            const categories = formData.content?.categories || [];
            const videos = formData.content?.videos || [];
            const businessData = formData.content?.business?.contact || {};

            return (
                <div className="bg-white min-h-full">
                    {/* Preview Header */}
                    <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                {uploadedFiles.brand_logo ? (
                                    <img
                                        src={previewImages.brand_logo}
                                        alt="Logo"
                                        className="w-12 h-12 object-contain rounded-lg bg-white p-1"
                                    />
                                ) : (
                                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                                        <ImageIcon className="w-6 h-6 text-gray-400" />
                                    </div>
                                )}
                                <div>
                                    <h1 className="text-xl font-bold">
                                        {brandData.name || 'Your Brand Name'}
                                    </h1>
                                    <p className="text-gray-300 text-sm">
                                        {brandData.tagline || 'Your Brand Tagline'}
                                    </p>
                                </div>
                            </div>
                            <nav className="hidden md:flex space-x-6">
                                <span className="hover:text-gray-300 cursor-pointer">Home</span>
                                <span className="hover:text-gray-300 cursor-pointer">Categories</span>
                                <span className="hover:text-gray-300 cursor-pointer">About</span>
                                <span className="hover:text-gray-300 cursor-pointer">Contact</span>
                            </nav>
                        </div>
                    </div>

                    {/* Hero Section Preview */}
                    {selectedTemplate?.sections.includes('hero') && (
                        <div className="relative h-64 bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white">
                            {uploadedFiles.hero_background && (
                                <img
                                    src={previewImages.hero_background}
                                    alt="Hero"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            )}
                            <div className="relative z-10 text-center p-8">
                                <h2 className="text-4xl font-bold mb-2">
                                    {heroData.title || 'Welcome to Fashion Excellence'}
                                </h2>
                                <p className="text-xl mb-4">
                                    {heroData.subtitle || 'Discover timeless elegance'}
                                </p>
                                {heroData.description && (
                                    <p className="text-sm mb-6 opacity-90">
                                        {heroData.description}
                                    </p>
                                )}
                                <button className="bg-white text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                                    {heroData.cta_text || 'Shop Collection'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Categories Preview */}
                    {selectedTemplate?.sections.includes('categories') && categories.length > 0 && (
                        <div className="p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Categories</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {categories.slice(0, 4).map((category, index) => (
                                    <div key={index} className="bg-gray-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow">
                                        <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-3 flex items-center justify-center">
                                            <Layers className="w-8 h-8 text-gray-400" />
                                        </div>
                                        <h4 className="font-medium text-gray-900">
                                            {category.name || `Category ${index + 1}`}
                                        </h4>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {category.description || 'Category description'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Videos Preview */}
                    {selectedTemplate?.sections.includes('videos') && videos.length > 0 && (
                        <div className="bg-gray-50 p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Video Gallery</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {videos.slice(0, 3).map((video, index) => (
                                    <div key={index} className="bg-white rounded-lg overflow-hidden shadow-sm">
                                        <div className="h-32 bg-gradient-to-r from-gray-400 to-gray-600 flex items-center justify-center">
                                            <PlayCircle className="w-12 h-12 text-white" />
                                        </div>
                                        <div className="p-4">
                                            <h4 className="font-medium text-gray-900">
                                                {video.title || `Video ${index + 1}`}
                                            </h4>
                                            <p className="text-sm text-gray-500 mt-1">
                                                {video.description || 'Video description'}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Brand Description */}
                    {brandData.description && (
                        <div className="p-8 bg-white">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">About Us</h3>
                            <div className="max-w-3xl mx-auto text-center">
                                <p className="text-gray-600 leading-relaxed">
                                    {brandData.description}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Contact Preview */}
                    {selectedTemplate?.sections.includes('business') && (
                        <div className="bg-gray-900 text-white p-8">
                            <h3 className="text-2xl font-bold mb-6 text-center">Contact Us</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto text-center">
                                <div>
                                    <div className="w-12 h-12 bg-blue-500 rounded-full mx-auto mb-3 flex items-center justify-center">
                                        <FileText className="w-6 h-6" />
                                    </div>
                                    <h4 className="font-medium mb-2">Email</h4>
                                    <p className="text-gray-300 text-sm">
                                        {businessData.email || 'contact@yourbusiness.com'}
                                    </p>
                                </div>
                                <div>
                                    <div className="w-12 h-12 bg-blue-500 rounded-full mx-auto mb-3 flex items-center justify-center">
                                        <FileText className="w-6 h-6" />
                                    </div>
                                    <h4 className="font-medium mb-2">Phone</h4>
                                    <p className="text-gray-300 text-sm">
                                        {businessData.phone || '+1 (555) 123-4567'}
                                    </p>
                                </div>
                                <div>
                                    <div className="w-12 h-12 bg-blue-500 rounded-full mx-auto mb-3 flex items-center justify-center">
                                        <FileText className="w-6 h-6" />
                                    </div>
                                    <h4 className="font-medium mb-2">Hours</h4>
                                    <p className="text-gray-300 text-sm">
                                        {businessData.working_hours || 'Mon-Sat: 10AM-8PM'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        };

        return (
            <div className="fixed inset-0 z-50 overflow-hidden">
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-black bg-opacity-50"
                    onClick={() => setShowPreviewModal(false)}
                ></div>

                {/* Modal */}
                <div className="relative h-full flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full h-5/6 flex flex-col">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b">
                            <div className="flex items-center space-x-4">
                                <h2 className="text-xl font-bold text-gray-900">Template Preview</h2>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => setPreviewMode('desktop')}
                                        className={`p-2 rounded-lg transition-colors ${
                                            previewMode === 'desktop'
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                        title="Desktop view"
                                    >
                                        <Monitor className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => setPreviewMode('tablet')}
                                        className={`p-2 rounded-lg transition-colors ${
                                            previewMode === 'tablet'
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                        title="Tablet view"
                                    >
                                        <Tablet className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => setPreviewMode('mobile')}
                                        className={`p-2 rounded-lg transition-colors ${
                                            previewMode === 'mobile'
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                        title="Mobile view"
                                    >
                                        <Smartphone className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Preview Content */}
                        <div className="flex-1 overflow-hidden bg-gray-100 p-6">
                            <div
                                className={`mx-auto bg-white shadow-lg transition-all duration-300 ${
                                    previewMode === 'mobile'
                                        ? 'max-w-sm h-full rounded-3xl'
                                        : previewMode === 'tablet'
                                            ? 'max-w-2xl h-full rounded-2xl'
                                            : 'w-full h-full rounded-xl'
                                }`}
                            >
                                <div className="h-full overflow-auto">
                                    {getPreviewContent()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Section renderer functions

    // Brand Section Renderer
    const renderBrandSection = () => (
        <div className="space-y-8">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">Brand Identity</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                    Define your brand's visual identity and core information that will represent your business across your website.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                    <ModernFileUpload
                        label="Brand Logo"
                        value={formData.content?.brand?.logo || ''}
                        onChange={(value) => handleInputChange('content.brand.logo', value)}
                        placeholder="Click to upload your brand logo"
                        required={true}
                        accept="image/*"
                        fileType="image"
                        dimensions="300x200px"
                        description="Your brand logo will appear across your template"
                        fieldPath="content.brand.logo"
                        validationRules={{ required: true }}
                    />

                    <ModernInput
                        label="Brand Name"
                        value={formData.content?.brand?.name || ''}
                        onChange={(value) => handleInputChange('content.brand.name', value)}
                        placeholder="Enter your brand name"
                        required={true}
                        icon={Type}
                        fieldPath="content.brand.name"
                        validationRules={{ required: true, minLength: 2, maxLength: 50 }}
                        validationErrors={validationErrors}
                        setValidationErrors={setValidationErrors}
                    />

                    <ModernInput
                        label="Tagline"
                        value={formData.content?.brand?.tagline || ''}
                        onChange={(value) => handleInputChange('content.brand.tagline', value)}
                        placeholder="Your brand tagline"
                        icon={Edit}
                        fieldPath="content.brand.tagline"
                        validationRules={{ maxLength: 100 }}
                        validationErrors={validationErrors}
                        setValidationErrors={setValidationErrors}
                    />

                    <ModernTextarea
                        label="Brand Description"
                        value={formData.content?.brand?.description || ''}
                        onChange={(value) => handleInputChange('content.brand.description', value)}
                        placeholder="Describe your brand, mission, and what makes you unique..."
                        required={true}
                        description="This will be used in your website's about section"
                        fieldPath="content.brand.description"
                        validationRules={{ required: true, minLength: 20, maxLength: 500 }}
                        validationErrors={validationErrors}
                        setValidationErrors={setValidationErrors}
                    />
                </div>

                <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-2">Primary Color</label>
                            <input
                                type="color"
                                value={formData.theme?.colors?.primary || '#000000'}
                                onChange={(e) => {
                                    const newColors = {
                                        ...(formData.theme?.colors || {
                                            primary: '#000000',
                                            secondary: '#ffffff',
                                            accent: '#f3f4f6',
                                            background: '#ffffff',
                                            text: '#111827'
                                        }),
                                        primary: e.target.value
                                    };
                                    console.log('🎨 Primary color changed to:', e.target.value, 'Full colors:', newColors);
                                    handleInputChange('theme.colors', newColors);
                                }}
                                className="w-full h-12 border border-gray-300 rounded-xl"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-2">Secondary Color</label>
                            <input
                                type="color"
                                value={formData.theme?.colors?.secondary || '#ffffff'}
                                onChange={(e) => {
                                    const newColors = {
                                        ...(formData.theme?.colors || {
                                            primary: '#000000',
                                            secondary: '#ffffff',
                                            accent: '#f3f4f6',
                                            background: '#ffffff',
                                            text: '#111827'
                                        }),
                                        secondary: e.target.value
                                    };
                                    console.log('🎨 Secondary color changed to:', e.target.value, 'Full colors:', newColors);
                                    handleInputChange('theme.colors', newColors);
                                }}
                                className="w-full h-12 border border-gray-300 rounded-xl"
                            />
                        </div>
                    </div>

                    <ModernSelect
                        label="Font Style"
                        value={formData.theme?.fonts?.heading || 'Playfair Display'}
                        onChange={(value) => handleInputChange('theme.fonts.heading', value)}
                        options={[
                            { value: 'Playfair Display', label: 'Playfair Display - Elegant' },
                            { value: 'Inter', label: 'Inter - Modern' },
                            { value: 'Poppins', label: 'Poppins - Friendly' },
                            { value: 'Montserrat', label: 'Montserrat - Professional' },
                            { value: 'Roboto', label: 'Roboto - Clean' }
                        ]}
                    />

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                            <Info className="w-4 h-4 mr-2" />
                            Owner Information (Optional)
                        </h4>
                        <div className="space-y-4">
                            <ModernInput
                                label="Owner Name"
                                value={formData.content?.brand?.owner?.name || ''}
                                onChange={(value) => handleInputChange('content.brand.owner.name', value)}
                                placeholder="Your name"
                                fieldPath="content.brand.owner.name"
                                validationRules={{ minLength: 2, maxLength: 50 }}
                            />
                            <ModernTextarea
                                label="Owner Bio"
                                value={formData.content?.brand?.owner?.bio || ''}
                                onChange={(value) => handleInputChange('content.brand.owner.bio', value)}
                                placeholder="Brief bio about the owner/founder"
                                rows={3}
                                fieldPath="content.brand.owner.bio"
                                validationRules={{ maxLength: 300 }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // State for expanded/collapsed items
    const [expandedItems, setExpandedItems] = useState({});

    // Enhanced array manipulation functions
    const duplicateArrayItem = useCallback((sectionId, index) => {
        const currentArray = formData.content?.[sectionId] || [];
        const itemToDuplicate = currentArray[index];
        if (!itemToDuplicate) return;

        const duplicatedItem = {
            ...itemToDuplicate,
            id: `${sectionId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: itemToDuplicate.name ? `${itemToDuplicate.name} (Copy)` : '',
            title: itemToDuplicate.title ? `${itemToDuplicate.title} (Copy)` : ''
        };

        setFormData(prev => ({
            ...prev,
            content: {
                ...prev.content,
                [sectionId]: [
                    ...currentArray.slice(0, index + 1),
                    duplicatedItem,
                    ...currentArray.slice(index + 1)
                ]
            }
        }));
    }, [formData]);

    const toggleItemExpansion = useCallback((sectionId, index) => {
        const key = `${sectionId}_${index}`;
        setExpandedItems(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    }, []);

    // Categories Section Renderer
    const renderCategoriesSection = () => {
        const categories = formData.content?.categories || [];

        const addCategoryItem = () => {
            const newCategory = {
                name: '',
                description: '',
                gender: 'All',
                type: 'All',
                category: 'All',
                image: '',
                id: `categories_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            };
            addArrayItem('categories', newCategory);
        };

        return (
            <div className="space-y-8">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Layers className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-3">Product Categories</h3>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        Create beautiful product categories to organize your inventory and help customers find what they're looking for.
                    </p>
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 max-w-2xl mx-auto">
                        <p className="text-blue-800 text-sm">
                            💡 <strong>Smart Categories:</strong> Select Gender and Type first - the Category dropdown will automatically show relevant options for your selection.
                        </p>
                    </div>
                </div>

                <div className="space-y-6">
                    {categories.map((category, index) => {
                        const isExpanded = expandedItems[`categories_${index}`] !== false; // Default to expanded
                        const hasContent = category.name || category.description || category.image;
                        const isComplete = category.name && category.description && category.image;

                        return (
                            <div key={category.id || index} className={`bg-gradient-to-br border rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 ${
                                isComplete ? 'from-green-50 to-emerald-50 border-green-200' :
                                hasContent ? 'from-yellow-50 to-orange-50 border-yellow-200' :
                                'from-gray-50 to-gray-100 border-gray-200'
                            }`}>
                                {/* Enhanced Header */}
                                <div className="flex justify-between items-center p-6 pb-4">
                                    <div className="flex items-center space-x-3">
                                        <span className={`w-8 h-8 text-white rounded-full flex items-center justify-center text-sm font-bold ${
                                            isComplete ? 'bg-green-500' : hasContent ? 'bg-yellow-500' : 'bg-blue-500'
                                        }`}>
                                            {index + 1}
                                        </span>
                                        <h4 className="text-lg font-semibold text-gray-900">
                                            {category.name || `Category ${index + 1}`}
                                        </h4>
                                        {isComplete && <CheckCircle className="w-5 h-5 text-green-500" />}
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        {/* Move Up/Down */}
                                        {index > 0 && (
                                            <button
                                                onClick={() => moveArrayItem('categories', index, index - 1)}
                                                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors"
                                                title="Move up"
                                            >
                                                <ChevronUp className="w-4 h-4" />
                                            </button>
                                        )}
                                        {index < categories.length - 1 && (
                                            <button
                                                onClick={() => moveArrayItem('categories', index, index + 1)}
                                                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors"
                                                title="Move down"
                                            >
                                                <ChevronDown className="w-4 h-4" />
                                            </button>
                                        )}

                                        {/* Duplicate */}
                                        {categories.length < 5 && (
                                            <button
                                                onClick={() => duplicateArrayItem('categories', index)}
                                                className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg transition-colors"
                                                title="Duplicate category"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        )}

                                        {/* Expand/Collapse */}
                                        <button
                                            onClick={() => toggleItemExpansion('categories', index)}
                                            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                            title={isExpanded ? "Collapse" : "Expand"}
                                        >
                                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </button>

                                        {/* Delete */}
                                        {categories.length > 1 && (
                                            <button
                                                onClick={() => removeArrayItem('categories', index)}
                                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                                                title="Delete category"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Content Area */}
                                {isExpanded && (
                                    <div className="px-6 pb-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                            <ModernInput
                                                label="Category Name"
                                                value={category.name || ''}
                                                onChange={(value) => handleInputChange(`content.categories.${index}.name`, value)}
                                                placeholder="e.g., Women's Clothing"
                                                required={true}
                                                fieldPath={`content.categories.${index}.name`}
                                                validationRules={{ required: true, minLength: 2, maxLength: 50 }}
                                                validationErrors={validationErrors}
                                                setValidationErrors={setValidationErrors}
                                            />

                                            <ModernInput
                                                label="Description"
                                                value={category.description || ''}
                                                onChange={(value) => handleInputChange(`content.categories.${index}.description`, value)}
                                                placeholder="Brief description"
                                                fieldPath={`content.categories.${index}.description`}
                                                validationRules={{ maxLength: 200 }}
                                                validationErrors={validationErrors}
                                                setValidationErrors={setValidationErrors}
                                            />

                                            <ModernSelect
                                                label="Gender"
                                                value={category.gender || 'All'}
                                                onChange={(value) => {
                                                    // Update gender
                                                    handleInputChange(`content.categories.${index}.gender`, value);
                                                    // Reset category when gender changes
                                                    handleInputChange(`content.categories.${index}.category`, 'All');
                                                }}
                                                options={GENDER_OPTIONS}
                                                placeholder="Select gender"
                                                validationErrors={validationErrors}
                                                setValidationErrors={setValidationErrors}
                                            />

                                            <ModernSelect
                                                label="Type"
                                                value={category.type || 'All'}
                                                onChange={(value) => {
                                                    // Update type
                                                    handleInputChange(`content.categories.${index}.type`, value);
                                                    // Reset category when type changes
                                                    handleInputChange(`content.categories.${index}.category`, 'All');
                                                }}
                                                options={TYPE_OPTIONS}
                                                placeholder="Select type"
                                                validationErrors={validationErrors}
                                                setValidationErrors={setValidationErrors}
                                            />

                                            <ModernSelect
                                                label="Category"
                                                value={category.category || 'All'}
                                                onChange={(value) => handleInputChange(`content.categories.${index}.category`, value)}
                                                options={getFilteredCategories(category.gender || 'All', category.type || 'All')}
                                                placeholder="Select category"
                                                validationErrors={validationErrors}
                                                setValidationErrors={setValidationErrors}
                                            />

                                            <div className="md:col-span-2 lg:col-span-3">
                                                <ModernFileUpload
                                                    label="Category Image"
                                                    value={category.image || ''}
                                                    onChange={(value) => handleInputChange(`content.categories.${index}.image`, value)}
                                                    placeholder="Click to upload category image"
                                                    accept="image/*"
                                                    fileType="image"
                                                    dimensions="400x300px recommended"
                                                    description="High-quality image representing this category"
                                                    fieldPath={`content.categories.${index}.image`}
                                                    validationRules={{}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Quick Preview when collapsed */}
                                {!isExpanded && hasContent && (
                                    <div className="px-6 pb-4">
                                        <div className="text-sm text-gray-600 bg-white bg-opacity-50 rounded-lg p-3">
                                            <div className="flex items-center space-x-4">
                                                {category.name && <span><strong>Name:</strong> {category.name}</span>}
                                                {category.gender && category.gender !== 'All' && <span><strong>Gender:</strong> {category.gender}</span>}
                                                {category.type && category.type !== 'All' && <span><strong>Type:</strong> {category.type}</span>}
                                                {category.category && category.category !== 'All' && <span><strong>Category:</strong> {category.category}</span>}
                                            </div>
                                            {category.description && (
                                                <div className="mt-1">
                                                    <strong>Description:</strong> {category.description.length > 50 ? `${category.description.substring(0, 50)}...` : category.description}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {categories.length < 5 && (
                        <button
                            onClick={addCategoryItem}
                            className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 group"
                        >
                            <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                            <span className="text-blue-600 group-hover:text-blue-700 font-medium">
                                Add Category ({categories.length + 1}/5)
                            </span>
                        </button>
                    )}
                </div>
            </div>
        );
    };


    // Videos Section Renderer
    const renderVideosSection = () => {
        const videos = formData.content?.videos || [];

        const addVideoItem = () => {
            const newVideo = {
                title: '',
                description: '',
                url: '',
                file: '',
                id: `videos_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            };
            addArrayItem('videos', newVideo);
        };

        return (
            <div className="space-y-8">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <PlayCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-3">Video Gallery</h3>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        Add engaging video content to showcase your products and brand story.
                    </p>
                </div>

                <div className="space-y-6">
                    {videos.map((video, index) => {
                        const isExpanded = expandedItems[`videos_${index}`] !== false; // Default to expanded
                        const hasContent = video.title || video.description || video.url || video.file;
                        const isComplete = video.title && (video.url || video.file);

                        return (
                            <div key={video.id || index} className={`bg-gradient-to-br border rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 ${
                                isComplete ? 'from-green-50 to-emerald-50 border-green-200' :
                                hasContent ? 'from-yellow-50 to-orange-50 border-yellow-200' :
                                'from-gray-50 to-gray-100 border-gray-200'
                            }`}>
                                {/* Enhanced Header */}
                                <div className="flex justify-between items-center p-6 pb-4">
                                    <div className="flex items-center space-x-3">
                                        <span className={`w-8 h-8 text-white rounded-full flex items-center justify-center text-sm font-bold ${
                                            isComplete ? 'bg-green-500' : hasContent ? 'bg-yellow-500' : 'bg-purple-500'
                                        }`}>
                                            {index + 1}
                                        </span>
                                        <h4 className="text-lg font-semibold text-gray-900">
                                            {video.title || `Video ${index + 1}`}
                                        </h4>
                                        {isComplete && <CheckCircle className="w-5 h-5 text-green-500" />}
                                        {video.url && <PlayCircle className="w-5 h-5 text-purple-500" />}
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        {/* Move Up/Down */}
                                        {index > 0 && (
                                            <button
                                                onClick={() => moveArrayItem('videos', index, index - 1)}
                                                className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg transition-colors"
                                                title="Move up"
                                            >
                                                <ChevronUp className="w-4 h-4" />
                                            </button>
                                        )}
                                        {index < videos.length - 1 && (
                                            <button
                                                onClick={() => moveArrayItem('videos', index, index + 1)}
                                                className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg transition-colors"
                                                title="Move down"
                                            >
                                                <ChevronDown className="w-4 h-4" />
                                            </button>
                                        )}

                                        {/* Duplicate */}
                                        {videos.length < 5 && (
                                            <button
                                                onClick={() => duplicateArrayItem('videos', index)}
                                                className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg transition-colors"
                                                title="Duplicate video"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        )}

                                        {/* Expand/Collapse */}
                                        <button
                                            onClick={() => toggleItemExpansion('videos', index)}
                                            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                            title={isExpanded ? "Collapse" : "Expand"}
                                        >
                                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </button>

                                        {/* Delete */}
                                        {videos.length > 1 && (
                                            <button
                                                onClick={() => removeArrayItem('videos', index)}
                                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                                                title="Delete video"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Content Area */}
                                {isExpanded && (
                                    <div className="px-6 pb-6">
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <ModernInput
                                                    label="Video Title"
                                                    value={video.title || ''}
                                                    onChange={(value) => handleInputChange(`content.videos.${index}.title`, value)}
                                                    placeholder="e.g., Collection Showcase"
                                                    required={true}
                                                    fieldPath={`content.videos.${index}.title`}
                                                    validationRules={{ required: true, minLength: 2, maxLength: 100 }}
                                                    validationErrors={validationErrors}
                                                    setValidationErrors={setValidationErrors}
                                                />
                                                <ModernInput
                                                    label="Video URL"
                                                    value={video.url || ''}
                                                    onChange={(value) => handleInputChange(`content.videos.${index}.url`, value)}
                                                    placeholder="https://youtube.com/watch?v=..."
                                                    description="YouTube, Vimeo, or direct video URL"
                                                    fieldPath={`content.videos.${index}.url`}
                                                    validationRules={{ url: true, maxLength: 500 }}
                                                    validationErrors={validationErrors}
                                                    setValidationErrors={setValidationErrors}
                                                />
                                            </div>

                                            <ModernTextarea
                                                label="Video Description"
                                                value={video.description || ''}
                                                onChange={(value) => handleInputChange(`content.videos.${index}.description`, value)}
                                                placeholder="Describe what this video showcases..."
                                                rows={3}
                                                fieldPath={`content.videos.${index}.description`}
                                                validationRules={{ maxLength: 300 }}
                                                validationErrors={validationErrors}
                                                setValidationErrors={setValidationErrors}
                                            />

                                            <ModernFileUpload
                                                label="Upload Video File"
                                                value={video.file || ''}
                                                onChange={(value) => handleInputChange(`content.videos.${index}.file`, value)}
                                                placeholder="Click to upload your video file"
                                                accept="video/*"
                                                fileType="video"
                                                dimensions="MP4, WebM, MOV recommended"
                                                description="Alternative to URL - upload video directly (max 100MB)"
                                                fieldPath={`content.videos.${index}.file`}
                                                validationRules={{}}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Quick Preview when collapsed */}
                                {!isExpanded && hasContent && (
                                    <div className="px-6 pb-4">
                                        <div className="text-sm text-gray-600 bg-white bg-opacity-50 rounded-lg p-3">
                                            <div className="flex items-center space-x-4">
                                                {video.title && <span><strong>Title:</strong> {video.title}</span>}
                                                {video.url && <span><strong>URL:</strong> {video.url.length > 30 ? `${video.url.substring(0, 30)}...` : video.url}</span>}
                                                {video.file && <span><strong>File:</strong> Uploaded</span>}
                                            </div>
                                            {video.description && (
                                                <div className="mt-1">
                                                    <strong>Description:</strong> {video.description.length > 50 ? `${video.description.substring(0, 50)}...` : video.description}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {videos.length < 5 && (
                        <button
                            onClick={addVideoItem}
                            className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                        >
                            <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                            <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                                Add Video ({videos.length + 1}/5)
                            </span>
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // Hero Section Renderer
    const renderHeroSection = () => (
        <div className="space-y-8">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Monitor className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">Hero Section</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                    Create an impactful first impression with your hero section that captures visitors' attention immediately.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                    <ModernInput
                        label="Hero Title"
                        value={formData.content?.hero?.title || ''}
                        onChange={(value) => handleInputChange('content.hero.title', value)}
                        placeholder="e.g., Welcome to Fashion Excellence"
                        required={true}
                        description="Main headline that visitors see first"
                    />

                    <ModernInput
                        label="Hero Subtitle"
                        value={formData.content?.hero?.subtitle || ''}
                        onChange={(value) => handleInputChange('content.hero.subtitle', value)}
                        placeholder="e.g., Discover timeless elegance"
                        description="Supporting text under the main title"
                    />

                    <ModernTextarea
                        label="Description"
                        value={formData.content?.hero?.description || ''}
                        onChange={(value) => handleInputChange('content.hero.description', value)}
                        placeholder="Additional descriptive text about your brand or current offers..."
                        rows={3}
                    />

                    <div className="grid grid-cols-2 gap-4">
                        <ModernInput
                            label="Call-to-Action Text"
                            value={formData.content?.hero?.cta_text || 'Shop Collection'}
                            onChange={(value) => handleInputChange('content.hero.cta_text', value)}
                            placeholder="Shop Now"
                        />
                        <ModernInput
                            label="CTA Link"
                            value={formData.content?.hero?.cta_link || '#products'}
                            onChange={(value) => handleInputChange('content.hero.cta_link', value)}
                            placeholder="#products"
                        />
                    </div>
                </div>

                <div className="space-y-6">
                    <ModernFileUpload
                        label="Hero Background Image"
                        value={formData.content?.hero?.background_image || ''}
                        onChange={(value) => handleInputChange('content.hero.background_image', value)}
                        placeholder="Click to upload hero background image"
                        required={true}
                        accept="image/*"
                        fileType="image"
                        dimensions="1920x800px recommended"
                        description="High-quality background image for your hero section"
                        fieldPath="content.hero.background_image"
                        validationRules={{ required: true }}
                    />

                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                        <h4 className="font-semibold text-purple-900 mb-3">Hero Style Options</h4>
                        <ModernSelect
                            label="Hero Layout Style"
                            value={formData.layout?.heroStyle || 'overlay'}
                            onChange={(value) => handleInputChange('layout.heroStyle', value)}
                            options={[
                                { value: 'minimal', label: 'Minimal - Clean and simple' },
                                { value: 'split', label: 'Split - Text and image side by side' },
                                { value: 'overlay', label: 'Overlay - Text over background image' },
                                { value: 'fullscreen', label: 'Fullscreen - Full viewport height' },
                                { value: 'slider', label: 'Slider - Multiple hero slides' },
                                { value: 'video', label: 'Video - Video background' }
                            ]}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    // Business Section Renderer
    const renderBusinessSection = () => (
        <div className="space-y-8">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">Business Information</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                    Provide your business contact details and information for customers to reach you.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                    <ModernInput
                        label="Business Name"
                        value={formData.content?.business?.contact?.business_name || ''}
                        onChange={(value) => handleInputChange('content.business.contact.business_name', value)}
                        placeholder="Official business name"
                        required={true}
                        icon={ShoppingBag}
                        fieldPath="content.business.contact.business_name"
                        validationRules={{ required: true, minLength: 2, maxLength: 100 }}
                    />

                    <ModernInput
                        label="Email Address"
                        type="email"
                        value={formData.content?.business?.contact?.email || ''}
                        onChange={(value) => handleInputChange('content.business.contact.email', value)}
                        placeholder="contact@yourbusiness.com"
                        required={true}
                        icon={FileText}
                        fieldPath="content.business.contact.email"
                        validationRules={{ required: true, email: true }}
                    />

                    <ModernInput
                        label="Phone Number"
                        type="tel"
                        value={formData.content?.business?.contact?.phone || ''}
                        onChange={(value) => handleInputChange('content.business.contact.phone', value)}
                        placeholder="+1 (555) 123-4567"
                        required={true}
                        fieldPath="content.business.contact.phone"
                        validationRules={{ required: true, phone: true }}
                    />
                </div>

                <div className="space-y-6">
                    <ModernInput
                        label="Working Hours"
                        value={formData.content?.business?.contact?.working_hours || 'Mon-Sat: 10AM-8PM'}
                        onChange={(value) => handleInputChange('content.business.contact.working_hours', value)}
                        placeholder="Mon-Fri 9AM-6PM"
                        fieldPath="content.business.contact.working_hours"
                        validationRules={{ maxLength: 100 }}
                    />

                    <ModernTextarea
                        label="Business Address"
                        value={formData.content?.business?.contact?.address || ''}
                        onChange={(value) => handleInputChange('content.business.contact.address', value)}
                        placeholder="123 Business Street, City, State, ZIP"
                        rows={3}
                        fieldPath="content.business.contact.address"
                        validationRules={{ maxLength: 300 }}
                        description="Full business address for customer visits"
                    />

                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <h4 className="font-semibold text-green-900 mb-3 flex items-center">
                            <Heart className="w-4 h-4 mr-2" />
                            Social Media Links
                        </h4>
                        <div className="space-y-3">
                            <ModernInput
                                label="Instagram"
                                value={formData.content?.brand?.owner?.social?.instagram || ''}
                                onChange={(value) => handleInputChange('content.brand.owner.social.instagram', value)}
                                placeholder="https://instagram.com/yourbrand"
                                fieldPath="content.brand.owner.social.instagram"
                                validationRules={{ url: true, maxLength: 200 }}
                            />
                            <ModernInput
                                label="Facebook"
                                value={formData.content?.brand?.owner?.social?.facebook || ''}
                                onChange={(value) => handleInputChange('content.brand.owner.social.facebook', value)}
                                placeholder="https://facebook.com/yourbrand"
                                fieldPath="content.brand.owner.social.facebook"
                                validationRules={{ url: true, maxLength: 200 }}
                            />
                            <ModernInput
                                label="WhatsApp"
                                value={formData.content?.brand?.owner?.social?.whatsapp || ''}
                                fieldPath="content.brand.owner.social.whatsapp"
                                validationRules={{ maxLength: 50 }}
                                onChange={(value) => handleInputChange('content.brand.owner.social.whatsapp', value)}
                                placeholder="+1234567890"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );


    // Generic section renderer for other sections
    const renderGenericArraySection = (sectionId) => {
        const sectionData = allSections.find(s => s.id === sectionId);
        const items = formData.content?.[sectionId] || [];

        const addGenericItem = () => {
            let newItem = { id: `${sectionId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` };

            // Set default values based on section type
            switch (sectionId) {
                case 'banners':
                    newItem = { ...newItem, name: '', description: '', image: '', gender: 'All', type: 'All', category: 'All' };
                    break;
                case 'sliding_banners':
                    newItem = { ...newItem, title: '', image: '' };
                    break;
                case 'promo_banners':
                    newItem = { ...newItem, text: '', sub_text: '', gender: 'All', type: 'All', category: 'All', image: '' };
                    break;
                case 'fashion_trends':
                    newItem = { ...newItem, text: '', gender: 'All', type: 'All', category: 'All', image: '' };
                    break;
                case 'collections':
                    newItem = { ...newItem, name: '', gender: 'All', type: 'All', category: 'All', image: '' };
                    break;
                case 'gallery':
                    newItem = { ...newItem, title: '', image: '' };
                    break;
                case 'instagram':
                    newItem = { ...newItem, caption: '', image: '' };
                    break;
                default:
                    newItem = { ...newItem, name: '', description: '', image: '' };
            }

            addArrayItem(sectionId, newItem);
        };

        const renderItemContent = (item, index) => {
            switch (sectionId) {
                case 'banners':
                    return (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ModernInput
                                    label="Banner Name"
                                    value={item.name || ''}
                                    onChange={(value) => handleInputChange(`content.${sectionId}.${index}.name`, value)}
                                    placeholder="e.g., Summer Collection"
                                    required={true}
                                />
                                <ModernInput
                                    label="Description"
                                    value={item.description || ''}
                                    onChange={(value) => handleInputChange(`content.${sectionId}.${index}.description`, value)}
                                    placeholder="Brief description"
                                />
                            </div>
                            <ModernFileUpload
                                label="Banner Image"
                                value={item.image || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.image`, value)}
                                placeholder="Click to upload banner image"
                                accept="image/*"
                                fileType="image"
                                dimensions="1200x400px recommended"
                                description="High-quality banner image"
                                fieldPath={`content.${sectionId}.${index}.image`}
                                validationRules={{}}
                            />
                        </div>
                    );
                case 'sliding_banners':
                    return (
                        <div className="space-y-6">
                            <ModernInput
                                label="Banner Title"
                                value={item.title || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.title`, value)}
                                placeholder="e.g., New Collection Launch"
                                required={true}
                            />
                            <ModernFileUpload
                                label="Sliding Banner Image"
                                value={item.image || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.image`, value)}
                                placeholder="Click to upload sliding banner image"
                                accept="image/*"
                                fileType="image"
                                dimensions="1920x600px recommended"
                                description="High-resolution banner for carousel"
                                fieldPath={`content.${sectionId}.${index}.image`}
                                validationRules={{}}
                            />
                        </div>
                    );
                case 'promo_banners':
                    return (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ModernInput
                                    label="Promo Text"
                                    value={item.text || ''}
                                    onChange={(value) => handleInputChange(`content.${sectionId}.${index}.text`, value)}
                                    placeholder="e.g., Summer Sale"
                                    required={true}
                                />
                                <ModernInput
                                    label="Sub Text"
                                    value={item.sub_text || ''}
                                    onChange={(value) => handleInputChange(`content.${sectionId}.${index}.sub_text`, value)}
                                    placeholder="e.g., Up to 60% Off"
                                />
                            </div>
                            <ModernFileUpload
                                label="Promo Banner Image"
                                value={item.image || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.image`, value)}
                                placeholder="Click to upload promo banner image"
                                accept="image/*"
                                fileType="image"
                                dimensions="800x400px recommended"
                                description="Promotional banner image"
                                fieldPath={`content.${sectionId}.${index}.image`}
                                validationRules={{}}
                            />
                        </div>
                    );
                case 'fashion_trends':
                    return (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ModernInput
                                    label="Trend Name"
                                    value={item.text || ''}
                                    onChange={(value) => handleInputChange(`content.${sectionId}.${index}.text`, value)}
                                    placeholder="e.g., Minimalist Chic"
                                    required={true}
                                />
                                <ModernSelect
                                    label="Gender"
                                    value={item.gender || 'All'}
                                    onChange={(value) => handleInputChange(`content.${sectionId}.${index}.gender`, value)}
                                    options={[
                                        { value: 'All', label: 'All Genders' },
                                        { value: 'Men', label: 'Men' },
                                        { value: 'Women', label: 'Women' },
                                        { value: 'Kids', label: 'Kids' }
                                    ]}
                                />
                            </div>
                            <ModernFileUpload
                                label="Trend Image"
                                value={item.image || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.image`, value)}
                                placeholder="Click to upload trend image"
                                accept="image/*"
                                fileType="image"
                                dimensions="600x600px recommended"
                                description="Image showcasing this fashion trend"
                                fieldPath={`content.${sectionId}.${index}.image`}
                                validationRules={{}}
                            />
                        </div>
                    );
                case 'collections':
                    return (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ModernInput
                                    label="Collection Name"
                                    value={item.name || ''}
                                    onChange={(value) => handleInputChange(`content.${sectionId}.${index}.name`, value)}
                                    placeholder="e.g., Elite Collection"
                                    required={true}
                                />
                                <ModernSelect
                                    label="Gender"
                                    value={item.gender || 'All'}
                                    onChange={(value) => handleInputChange(`content.${sectionId}.${index}.gender`, value)}
                                    options={[
                                        { value: 'All', label: 'All Genders' },
                                        { value: 'Men', label: 'Men' },
                                        { value: 'Women', label: 'Women' },
                                        { value: 'Kids', label: 'Kids' }
                                    ]}
                                />
                            </div>
                            <ModernFileUpload
                                label="Collection Image"
                                value={item.image || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.image`, value)}
                                placeholder="Click to upload collection image"
                                accept="image/*"
                                fileType="image"
                                dimensions="800x600px recommended"
                                description="Main image for this collection"
                                fieldPath={`content.${sectionId}.${index}.image`}
                                validationRules={{}}
                            />
                        </div>
                    );
                case 'gallery':
                    return (
                        <div className="space-y-6">
                            <ModernInput
                                label="Image Title"
                                value={item.title || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.title`, value)}
                                placeholder="e.g., Portrait Series"
                                required={true}
                            />
                            <ModernFileUpload
                                label="Gallery Image"
                                value={item.image || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.image`, value)}
                                placeholder="Click to upload gallery image"
                                accept="image/*"
                                fileType="image"
                                dimensions="600x600px recommended"
                                description={`Gallery image ${index + 1}`}
                                fieldPath={`content.${sectionId}.${index}.image`}
                                validationRules={{}}
                            />
                        </div>
                    );
                case 'instagram':
                    return (
                        <div className="space-y-6">
                            <ModernInput
                                label="Instagram Caption"
                                value={item.caption || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.caption`, value)}
                                placeholder="e.g., Daily inspiration"
                                required={true}
                            />
                            <ModernFileUpload
                                label="Instagram Image"
                                value={item.image || ''}
                                onChange={(value) => handleInputChange(`content.${sectionId}.${index}.image`, value)}
                                placeholder="Click to upload Instagram image"
                                accept="image/*"
                                fileType="image"
                                dimensions="1080x1080px recommended"
                                description="Square image for Instagram feed"
                                fieldPath={`content.${sectionId}.${index}.image`}
                                validationRules={{}}
                            />
                        </div>
                    );
                default:
                    return (
                        <ModernInput
                            label="Name"
                            value={item.name || ''}
                            onChange={(value) => handleInputChange(`content.${sectionId}.${index}.name`, value)}
                            placeholder="Enter name"
                            required={true}
                        />
                    );
            }
        };

        return (
            <div className="space-y-8">
                <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 mb-3">{sectionData?.name || sectionId}</h3>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        {sectionData?.description || `Add items for ${sectionId}`}
                    </p>
                </div>

                <div className="space-y-6">
                    {items.map((item, index) => (
                        <div key={item.id || index} className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <span className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                                        {index + 1}
                                    </span>
                                    {sectionData?.name || sectionId} {index + 1}
                                </h4>
                                {items.length > 1 && (
                                    <button
                                        onClick={() => removeArrayItem(sectionId, index)}
                                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                                        title="Delete item"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {renderItemContent(item, index)}
                        </div>
                    ))}

                    {items.length < 5 && (
                        <button
                            onClick={addGenericItem}
                            className="w-full border-2 border-dashed border-indigo-300 rounded-2xl p-8 hover:border-indigo-400 hover:bg-indigo-50 transition-all duration-300 group"
                        >
                            <Plus className="w-8 h-8 mx-auto text-indigo-400 group-hover:text-indigo-600 mb-3" />
                            <span className="text-indigo-600 group-hover:text-indigo-700 font-medium">
                                Add {sectionData?.name || sectionId} ({items.length + 1}/5)
                            </span>
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // Current section content renderer
    const renderCurrentSectionContent = () => {
        switch (currentSection) {
            case 'brand':
                return renderBrandSection();
            case 'hero':
                return renderHeroSection();
            case 'categories':
                return renderCategoriesSection();
            case 'videos':
                return renderVideosSection();
            case 'business':
                return renderBusinessSection();
            case 'statement':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Edit className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Brand Statement</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Create powerful brand messaging that resonates with your audience.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernTextarea
                                    label="Main Statement"
                                    value={formData.content?.statement?.title || ''}
                                    onChange={(value) => handleInputChange('content.statement.title', value)}
                                    placeholder="Write your brand's main statement or mission..."
                                    rows={4}
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Supporting Text"
                                    value={formData.content?.statement?.subtitle || ''}
                                    onChange={(value) => handleInputChange('content.statement.subtitle', value)}
                                    placeholder="Additional supporting text for your statement..."
                                    rows={3}
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="statement_background_image"
                                    label="Statement Background Image"
                                    dimensions="1920x800px"
                                    description="Background image for the statement section"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'workshop':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Camera className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Workshop Information</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your workshop, studio, or behind-the-scenes content.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Workshop Title"
                                    value={formData.content?.workshop?.title || ''}
                                    onChange={(value) => handleInputChange('content.workshop.title', value)}
                                    placeholder="e.g., Our Artisan Workshop"
                                    required={true}
                                />
                                <ModernInput
                                    label="Location"
                                    value={formData.content?.workshop?.location || ''}
                                    onChange={(value) => handleInputChange('content.workshop.location', value)}
                                    placeholder="e.g., Mumbai, India"
                                />
                                <ModernTextarea
                                    label="Workshop Description"
                                    value={formData.content?.workshop?.description || ''}
                                    onChange={(value) => handleInputChange('content.workshop.description', value)}
                                    placeholder="Describe your workshop, processes, and craftsmanship..."
                                    rows={4}
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="workshop_image"
                                    label="Workshop Image"
                                    dimensions="800x600px"
                                    description="Image showcasing your workshop"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'craftsmanship':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Settings className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Craftsmanship</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your expertise and craft with compelling statistics.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.craftsmanship?.title || ''}
                                    onChange={(value) => handleInputChange('content.craftsmanship.title', value)}
                                    placeholder="e.g., Master Craftsmanship"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.craftsmanship?.description || ''}
                                    onChange={(value) => handleInputChange('content.craftsmanship.description', value)}
                                    placeholder="Describe your craftsmanship process and expertise..."
                                    rows={4}
                                    required={true}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <ModernInput
                                        label="Stat 1 Label"
                                        value={formData.content?.craftsmanship?.stat1_label || ''}
                                        onChange={(value) => handleInputChange('content.craftsmanship.stat1_label', value)}
                                        placeholder="e.g., Years Experience"
                                    />
                                    <ModernInput
                                        label="Stat 1 Value"
                                        value={formData.content?.craftsmanship?.stat1_value || ''}
                                        onChange={(value) => handleInputChange('content.craftsmanship.stat1_value', value)}
                                        placeholder="e.g., 25+"
                                    />
                                    <ModernInput
                                        label="Stat 2 Label"
                                        value={formData.content?.craftsmanship?.stat2_label || ''}
                                        onChange={(value) => handleInputChange('content.craftsmanship.stat2_label', value)}
                                        placeholder="e.g., Products Made"
                                    />
                                    <ModernInput
                                        label="Stat 2 Value"
                                        value={formData.content?.craftsmanship?.stat2_value || ''}
                                        onChange={(value) => handleInputChange('content.craftsmanship.stat2_value', value)}
                                        placeholder="e.g., 10,000+"
                                    />
                                    <ModernInput
                                        label="Stat 3 Label"
                                        value={formData.content?.craftsmanship?.stat3_label || ''}
                                        onChange={(value) => handleInputChange('content.craftsmanship.stat3_label', value)}
                                        placeholder="e.g., Artisans"
                                    />
                                    <ModernInput
                                        label="Stat 3 Value"
                                        value={formData.content?.craftsmanship?.stat3_value || ''}
                                        onChange={(value) => handleInputChange('content.craftsmanship.stat3_value', value)}
                                        placeholder="e.g., 50+"
                                    />
                                    <ModernInput
                                        label="Stat 4 Label"
                                        value={formData.content?.craftsmanship?.stat4_label || ''}
                                        onChange={(value) => handleInputChange('content.craftsmanship.stat4_label', value)}
                                        placeholder="e.g., Countries Shipped"
                                    />
                                    <ModernInput
                                        label="Stat 4 Value"
                                        value={formData.content?.craftsmanship?.stat4_value || ''}
                                        onChange={(value) => handleInputChange('content.craftsmanship.stat4_value', value)}
                                        placeholder="e.g., 30+"
                                    />
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="craftsmanship_image"
                                    label="Craftsmanship Image"
                                    dimensions="1200x800px"
                                    description="Image showcasing your craftsmanship process"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'designer_showcase':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Star className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Designer Showcase</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Feature your talented design team and their expertise.</p>
                        </div>
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.designer_showcase?.title || ''}
                                    onChange={(value) => handleInputChange('content.designer_showcase.title', value)}
                                    placeholder="e.g., Meet Our Designers"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.designer_showcase?.description || ''}
                                    onChange={(value) => handleInputChange('content.designer_showcase.description', value)}
                                    placeholder="Introduce your design team..."
                                    rows={3}
                                    required={true}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[1, 2, 3, 4, 5].map((num) => (
                                    <div key={num} className="bg-gray-50 p-6 rounded-lg">
                                        <h4 className="text-lg font-semibold mb-4">Designer {num}</h4>
                                        <div className="space-y-4">
                                            <ModernInput
                                                label="Name"
                                                value={formData.content?.designer_showcase?.[`designer${num}_name`] || ''}
                                                onChange={(value) => handleInputChange(`content.designer_showcase.designer${num}_name`, value)}
                                                placeholder="Designer Name"
                                            />
                                            <FileUploadArea
                                                fieldName={`designer${num}_image`}
                                                label="Designer Photo"
                                                dimensions="150x150px"
                                                description="Profile photo"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'media_showcase':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Monitor className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Media Showcase</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your media with impactful statistics and visuals.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.media_showcase?.title || ''}
                                    onChange={(value) => handleInputChange('content.media_showcase.title', value)}
                                    placeholder="e.g., Our Impact"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.media_showcase?.description || ''}
                                    onChange={(value) => handleInputChange('content.media_showcase.description', value)}
                                    placeholder="Describe your media showcase..."
                                    rows={4}
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Text Content"
                                    value={formData.content?.media_showcase?.text_content || ''}
                                    onChange={(value) => handleInputChange('content.media_showcase.text_content', value)}
                                    placeholder="Additional text content..."
                                    rows={3}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <ModernInput
                                        label="Stat 1 Label"
                                        value={formData.content?.media_showcase?.stat1_label || ''}
                                        onChange={(value) => handleInputChange('content.media_showcase.stat1_label', value)}
                                        placeholder="e.g., Happy Customers"
                                    />
                                    <ModernInput
                                        label="Stat 1 Value"
                                        value={formData.content?.media_showcase?.stat1_value || ''}
                                        onChange={(value) => handleInputChange('content.media_showcase.stat1_value', value)}
                                        placeholder="e.g., 10,000+"
                                    />
                                    <ModernInput
                                        label="Stat 2 Label"
                                        value={formData.content?.media_showcase?.stat2_label || ''}
                                        onChange={(value) => handleInputChange('content.media_showcase.stat2_label', value)}
                                        placeholder="e.g., Projects Completed"
                                    />
                                    <ModernInput
                                        label="Stat 2 Value"
                                        value={formData.content?.media_showcase?.stat2_value || ''}
                                        onChange={(value) => handleInputChange('content.media_showcase.stat2_value', value)}
                                        placeholder="e.g., 500+"
                                    />
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="media_showcase_large_image"
                                    label="Large Showcase Image"
                                    dimensions="1200x800px"
                                    description="Main image for media showcase"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'portfolio_showcase':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Layers className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Portfolio Showcase</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Display your best work and featured projects.</p>
                        </div>
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.portfolio_showcase?.title || ''}
                                    onChange={(value) => handleInputChange('content.portfolio_showcase.title', value)}
                                    placeholder="e.g., Our Portfolio"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.portfolio_showcase?.description || ''}
                                    onChange={(value) => handleInputChange('content.portfolio_showcase.description', value)}
                                    placeholder="Describe your portfolio..."
                                    rows={3}
                                    required={true}
                                />
                            </div>
                            <div className="bg-blue-50 p-6 rounded-lg">
                                <h4 className="text-xl font-semibold mb-4">Featured Project</h4>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <ModernInput
                                            label="Project Title"
                                            value={formData.content?.portfolio_showcase?.featured_title || ''}
                                            onChange={(value) => handleInputChange('content.portfolio_showcase.featured_title', value)}
                                            placeholder="Featured project name"
                                            required={true}
                                        />
                                        <ModernTextarea
                                            label="Description"
                                            value={formData.content?.portfolio_showcase?.featured_description || ''}
                                            onChange={(value) => handleInputChange('content.portfolio_showcase.featured_description', value)}
                                            placeholder="Project description..."
                                            rows={3}
                                            required={true}
                                        />
                                        <div className="grid grid-cols-3 gap-4">
                                            <ModernInput
                                                label="Category"
                                                value={formData.content?.portfolio_showcase?.featured_category || ''}
                                                onChange={(value) => handleInputChange('content.portfolio_showcase.featured_category', value)}
                                                placeholder="e.g., Fashion"
                                            />
                                            <ModernInput
                                                label="Client"
                                                value={formData.content?.portfolio_showcase?.featured_client || ''}
                                                onChange={(value) => handleInputChange('content.portfolio_showcase.featured_client', value)}
                                                placeholder="e.g., Acme Corp"
                                            />
                                            <ModernInput
                                                label="Year"
                                                value={formData.content?.portfolio_showcase?.featured_year || ''}
                                                onChange={(value) => handleInputChange('content.portfolio_showcase.featured_year', value)}
                                                placeholder="e.g., 2024"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <FileUploadArea
                                            fieldName="portfolio_featured_image"
                                            label="Featured Project Image"
                                            dimensions="1200x600px"
                                            description="Main project image"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {[1, 2, 3].map((num) => (
                                    <div key={num} className="bg-gray-50 p-6 rounded-lg">
                                        <h4 className="text-lg font-semibold mb-4">Project {num}</h4>
                                        <div className="space-y-4">
                                            <ModernInput
                                                label="Title"
                                                value={formData.content?.portfolio_showcase?.[`project${num}_title`] || ''}
                                                onChange={(value) => handleInputChange(`content.portfolio_showcase.project${num}_title`, value)}
                                                placeholder="Project title"
                                            />
                                            <ModernInput
                                                label="Category"
                                                value={formData.content?.portfolio_showcase?.[`project${num}_category`] || ''}
                                                onChange={(value) => handleInputChange(`content.portfolio_showcase.project${num}_category`, value)}
                                                placeholder="Category"
                                            />
                                            <ModernInput
                                                label="Year"
                                                value={formData.content?.portfolio_showcase?.[`project${num}_year`] || ''}
                                                onChange={(value) => handleInputChange(`content.portfolio_showcase.project${num}_year`, value)}
                                                placeholder="Year"
                                            />
                                            <FileUploadArea
                                                fieldName={`portfolio_project${num}_image`}
                                                label="Project Image"
                                                dimensions="800x600px"
                                                description="Project image"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'product_spotlight':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Zap className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Product Spotlight</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Highlight your featured product with pricing and details.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Product Title"
                                    value={formData.content?.product_spotlight?.title || ''}
                                    onChange={(value) => handleInputChange('content.product_spotlight.title', value)}
                                    placeholder="e.g., Premium Collection Dress"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.product_spotlight?.description || ''}
                                    onChange={(value) => handleInputChange('content.product_spotlight.description', value)}
                                    placeholder="Describe the featured product..."
                                    rows={4}
                                    required={true}
                                />
                                <div className="grid grid-cols-3 gap-4">
                                    <ModernInput
                                        label="Price"
                                        value={formData.content?.product_spotlight?.price || ''}
                                        onChange={(value) => handleInputChange('content.product_spotlight.price', value)}
                                        placeholder="e.g., ₹2,999"
                                    />
                                    <ModernInput
                                        label="Original Price"
                                        value={formData.content?.product_spotlight?.original_price || ''}
                                        onChange={(value) => handleInputChange('content.product_spotlight.original_price', value)}
                                        placeholder="e.g., ₹4,999"
                                    />
                                    <ModernInput
                                        label="Discount"
                                        value={formData.content?.product_spotlight?.discount || ''}
                                        onChange={(value) => handleInputChange('content.product_spotlight.discount', value)}
                                        placeholder="e.g., 40% OFF"
                                    />
                                </div>
                                <div className="space-y-4">
                                    <h4 className="text-lg font-semibold">Product Features</h4>
                                    <ModernInput
                                        label="Feature 1"
                                        value={formData.content?.product_spotlight?.feature1 || ''}
                                        onChange={(value) => handleInputChange('content.product_spotlight.feature1', value)}
                                        placeholder="e.g., Premium Fabric"
                                    />
                                    <ModernInput
                                        label="Feature 2"
                                        value={formData.content?.product_spotlight?.feature2 || ''}
                                        onChange={(value) => handleInputChange('content.product_spotlight.feature2', value)}
                                        placeholder="e.g., Handmade Design"
                                    />
                                    <ModernInput
                                        label="Feature 3"
                                        value={formData.content?.product_spotlight?.feature3 || ''}
                                        onChange={(value) => handleInputChange('content.product_spotlight.feature3', value)}
                                        placeholder="e.g., Limited Edition"
                                    />
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="product_spotlight_image"
                                    label="Product Image"
                                    dimensions="800x800px"
                                    description="Featured product image"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'newsletter':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Edit className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Newsletter</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Create a newsletter signup section to engage your audience.</p>
                        </div>
                        <div className="max-w-2xl mx-auto space-y-6">
                            <ModernInput
                                label="Section Title"
                                value={formData.content?.newsletter?.title || ''}
                                onChange={(value) => handleInputChange('content.newsletter.title', value)}
                                placeholder="e.g., Stay Connected"
                                required={true}
                            />
                            <ModernTextarea
                                label="Description"
                                value={formData.content?.newsletter?.description || ''}
                                onChange={(value) => handleInputChange('content.newsletter.description', value)}
                                placeholder="Encourage visitors to subscribe to your newsletter..."
                                rows={4}
                                required={true}
                            />
                        </div>
                    </div>
                );
            case 'brand_story':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Heart className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Brand Story</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Tell your brand story with compelling statistics and values.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Story Title"
                                    value={formData.content?.brand_story?.title || ''}
                                    onChange={(value) => handleInputChange('content.brand_story.title', value)}
                                    placeholder="e.g., Crafted with intention"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Story Description"
                                    value={formData.content?.brand_story?.description || ''}
                                    onChange={(value) => handleInputChange('content.brand_story.description', value)}
                                    placeholder="Tell your brand story and values..."
                                    rows={4}
                                    required={true}
                                />
                            </div>
                            <div className="space-y-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Statistics (optional)</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <ModernInput
                                        label="Stat 1 Label"
                                        value={formData.content?.brand_story?.stat1_label || ''}
                                        onChange={(value) => handleInputChange('content.brand_story.stat1_label', value)}
                                        placeholder="e.g., Sustainable"
                                    />
                                    <ModernInput
                                        label="Stat 1 Value"
                                        value={formData.content?.brand_story?.stat1_value || ''}
                                        onChange={(value) => handleInputChange('content.brand_story.stat1_value', value)}
                                        placeholder="e.g., 100%"
                                    />
                                    <ModernInput
                                        label="Stat 2 Label"
                                        value={formData.content?.brand_story?.stat2_label || ''}
                                        onChange={(value) => handleInputChange('content.brand_story.stat2_label', value)}
                                        placeholder="e.g., Quality"
                                    />
                                    <ModernInput
                                        label="Stat 2 Value"
                                        value={formData.content?.brand_story?.stat2_value || ''}
                                        onChange={(value) => handleInputChange('content.brand_story.stat2_value', value)}
                                        placeholder="e.g., Premium"
                                    />
                                    <ModernInput
                                        label="Stat 3 Label"
                                        value={formData.content?.brand_story?.stat3_label || ''}
                                        onChange={(value) => handleInputChange('content.brand_story.stat3_label', value)}
                                        placeholder="e.g., Timeless"
                                    />
                                    <ModernInput
                                        label="Stat 3 Value"
                                        value={formData.content?.brand_story?.stat3_value || ''}
                                        onChange={(value) => handleInputChange('content.brand_story.stat3_value', value)}
                                        placeholder="e.g., Always"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'video_story':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <PlayCircle className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Video Story</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Add a video section to showcase your brand story.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.video_story?.title || ''}
                                    onChange={(value) => handleInputChange('content.video_story.title', value)}
                                    placeholder="e.g., Our Story"
                                    required={true}
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="video_story_image"
                                    label="Video Thumbnail"
                                    dimensions="1200x675px"
                                    description="Upload a thumbnail for your video section"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'luxury_services':
                return renderGenericArraySection('luxury_services');
            case 'premium_collections':
                return renderGenericArraySection('premium_collections');
            case 'heritage':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Settings className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Heritage</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your brand's heritage and timeline.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Heritage Title"
                                    value={formData.content?.heritage?.title || ''}
                                    onChange={(value) => handleInputChange('content.heritage.title', value)}
                                    placeholder="e.g., Our Legacy"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Heritage Description"
                                    value={formData.content?.heritage?.description || ''}
                                    onChange={(value) => handleInputChange('content.heritage.description', value)}
                                    placeholder="Tell your brand's heritage story..."
                                    rows={4}
                                    required={true}
                                />
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Timeline (optional)</h4>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <ModernInput
                                            label="Milestone 1 Year"
                                            value={formData.content?.heritage?.year1 || ''}
                                            onChange={(value) => handleInputChange('content.heritage.year1', value)}
                                            placeholder="e.g., 1990"
                                        />
                                        <ModernInput
                                            label="Milestone 1"
                                            value={formData.content?.heritage?.milestone1 || ''}
                                            onChange={(value) => handleInputChange('content.heritage.milestone1', value)}
                                            placeholder="e.g., Founded the company"
                                        />
                                        <ModernInput
                                            label="Milestone 2 Year"
                                            value={formData.content?.heritage?.year2 || ''}
                                            onChange={(value) => handleInputChange('content.heritage.year2', value)}
                                            placeholder="e.g., 2005"
                                        />
                                        <ModernInput
                                            label="Milestone 2"
                                            value={formData.content?.heritage?.milestone2 || ''}
                                            onChange={(value) => handleInputChange('content.heritage.milestone2', value)}
                                            placeholder="e.g., First international expansion"
                                        />
                                        <ModernInput
                                            label="Milestone 3 Year"
                                            value={formData.content?.heritage?.year3 || ''}
                                            onChange={(value) => handleInputChange('content.heritage.year3', value)}
                                            placeholder="e.g., 2020"
                                        />
                                        <ModernInput
                                            label="Milestone 3"
                                            value={formData.content?.heritage?.milestone3 || ''}
                                            onChange={(value) => handleInputChange('content.heritage.milestone3', value)}
                                            placeholder="e.g., Sustainable practices initiative"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="heritage_image"
                                    label="Heritage Image"
                                    dimensions="1200x800px"
                                    description="Upload an image representing your heritage"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'events':
                return renderGenericArraySection('events');
            case 'human_values':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Users className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Human Values</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your team and company values.</p>
                        </div>
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.human_values?.title || ''}
                                    onChange={(value) => handleInputChange('content.human_values.title', value)}
                                    placeholder="e.g., Meet the Team"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Section Description"
                                    value={formData.content?.human_values?.description || ''}
                                    onChange={(value) => handleInputChange('content.human_values.description', value)}
                                    placeholder="Describe your team and values..."
                                    rows={4}
                                    required={true}
                                />
                            </div>
                            <div className="space-y-6">
                                <h4 className="text-lg font-semibold text-gray-900">Team Members (optional)</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {[1, 2, 3].map(num => (
                                        <div key={num} className="bg-gray-50 rounded-lg p-6">
                                            <h5 className="text-md font-semibold text-gray-900 mb-4">Team Member {num}</h5>
                                            <div className="space-y-4">
                                                <ModernInput
                                                    label="Name"
                                                    value={formData.content?.human_values?.[`team_member${num}_name`] || ''}
                                                    onChange={(value) => handleInputChange(`content.human_values.team_member${num}_name`, value)}
                                                    placeholder="e.g., John Doe"
                                                />
                                                <ModernInput
                                                    label="Role"
                                                    value={formData.content?.human_values?.[`team_member${num}_role`] || ''}
                                                    onChange={(value) => handleInputChange(`content.human_values.team_member${num}_role`, value)}
                                                    placeholder="e.g., Lead Designer"
                                                />
                                                <FileUploadArea
                                                    fieldName={`team_member_${num}_image`}
                                                    label="Profile Photo"
                                                    dimensions="300x300px"
                                                    description="Upload team member photo"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'environmental_impact':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Leaf className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Environmental Impact</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your sustainability initiatives and environmental impact.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.environmental_impact?.title || ''}
                                    onChange={(value) => handleInputChange('content.environmental_impact.title', value)}
                                    placeholder="e.g., Our Environmental Commitment"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.environmental_impact?.description || ''}
                                    onChange={(value) => handleInputChange('content.environmental_impact.description', value)}
                                    placeholder="Describe your environmental initiatives..."
                                    rows={4}
                                    required={true}
                                />
                                <div className="space-y-4">
                                    <h4 className="text-lg font-semibold text-gray-900">Impact Areas (optional)</h4>
                                    <div className="grid grid-cols-1 gap-4">
                                        {[1, 2, 3].map(num => (
                                            <div key={num} className="bg-gray-50 rounded-lg p-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <ModernInput
                                                        label={`Impact ${num} Title`}
                                                        value={formData.content?.environmental_impact?.[`impact${num}_title`] || ''}
                                                        onChange={(value) => handleInputChange(`content.environmental_impact.impact${num}_title`, value)}
                                                        placeholder="e.g., Carbon Neutral"
                                                    />
                                                    <ModernInput
                                                        label={`Impact ${num} Description`}
                                                        value={formData.content?.environmental_impact?.[`impact${num}_description`] || ''}
                                                        onChange={(value) => handleInputChange(`content.environmental_impact.impact${num}_description`, value)}
                                                        placeholder="Brief description"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="environmental_image"
                                    label="Environmental Image"
                                    dimensions="1200x600px"
                                    description="Upload sustainability-related image"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'quality_assurance':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Quality Assurance</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Highlight your quality processes and guarantees.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Quality Title"
                                    value={formData.content?.quality_assurance?.title || ''}
                                    onChange={(value) => handleInputChange('content.quality_assurance.title', value)}
                                    placeholder="e.g., Our Quality Promise"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Quality Description"
                                    value={formData.content?.quality_assurance?.description || ''}
                                    onChange={(value) => handleInputChange('content.quality_assurance.description', value)}
                                    placeholder="Describe your quality assurance process..."
                                    rows={4}
                                    required={true}
                                />
                                <div className="space-y-4">
                                    <h4 className="text-lg font-semibold text-gray-900">Quality Processes (optional)</h4>
                                    <div className="space-y-3">
                                        {[1, 2, 3].map(num => (
                                            <ModernInput
                                                key={num}
                                                label={`Process ${num}`}
                                                value={formData.content?.quality_assurance?.[`process${num}`] || ''}
                                                onChange={(value) => handleInputChange(`content.quality_assurance.process${num}`, value)}
                                                placeholder={`Quality process step ${num}...`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="quality_image"
                                    label="Quality Process Image"
                                    dimensions="1200x800px"
                                    description="Upload image showing quality processes"
                                />
                            </div>
                        </div>
                    </div>
                );

            // Template 18 - FOCAL (Minimalist E-commerce)
            case 'quality_craftsmanship':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Wrench className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Quality Craftsmanship</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your commitment to quality, simplicity, and performance.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.quality_craftsmanship?.title || ''}
                                    onChange={(value) => handleInputChange('content.quality_craftsmanship.title', value)}
                                    placeholder="e.g., Quality is our focus"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Main Description"
                                    value={formData.content?.quality_craftsmanship?.description || ''}
                                    onChange={(value) => handleInputChange('content.quality_craftsmanship.description', value)}
                                    placeholder="Describe your quality philosophy and craftsmanship..."
                                    rows={4}
                                    required={true}
                                />
                                <div className="bg-blue-50 p-6 rounded-lg">
                                    <h4 className="text-lg font-semibold mb-4">Core Values</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div className="space-y-3">
                                            <h5 className="font-medium">Value 1</h5>
                                            <ModernInput
                                                label="Value Title"
                                                value={formData.content?.quality_craftsmanship?.value1_title || ''}
                                                onChange={(value) => handleInputChange('content.quality_craftsmanship.value1_title', value)}
                                                placeholder="e.g., Quality"
                                            />
                                            <ModernInput
                                                label="Value Description"
                                                value={formData.content?.quality_craftsmanship?.value1_desc || ''}
                                                onChange={(value) => handleInputChange('content.quality_craftsmanship.value1_desc', value)}
                                                placeholder="e.g., Premium materials and craftsmanship"
                                            />
                                        </div>
                                        <div className="space-y-3">
                                            <h5 className="font-medium">Value 2</h5>
                                            <ModernInput
                                                label="Value Title"
                                                value={formData.content?.quality_craftsmanship?.value2_title || ''}
                                                onChange={(value) => handleInputChange('content.quality_craftsmanship.value2_title', value)}
                                                placeholder="e.g., Simplicity"
                                            />
                                            <ModernInput
                                                label="Value Description"
                                                value={formData.content?.quality_craftsmanship?.value2_desc || ''}
                                                onChange={(value) => handleInputChange('content.quality_craftsmanship.value2_desc', value)}
                                                placeholder="e.g., Clean design focused on essentials"
                                            />
                                        </div>
                                        <div className="space-y-3">
                                            <h5 className="font-medium">Value 3</h5>
                                            <ModernInput
                                                label="Value Title"
                                                value={formData.content?.quality_craftsmanship?.value3_title || ''}
                                                onChange={(value) => handleInputChange('content.quality_craftsmanship.value3_title', value)}
                                                placeholder="e.g., Performance"
                                            />
                                            <ModernInput
                                                label="Value Description"
                                                value={formData.content?.quality_craftsmanship?.value3_desc || ''}
                                                onChange={(value) => handleInputChange('content.quality_craftsmanship.value3_desc', value)}
                                                placeholder="e.g., Built to last and exceed expectations"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="quality_craftsmanship_image"
                                    label="Quality Image"
                                    dimensions="1200x800px"
                                    description="Image showcasing your quality and craftsmanship"
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'featured_products':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-gray-600 to-black rounded-full flex items-center justify-center mx-auto mb-4">
                                <Package className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Featured Products</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your best products with clean, minimal presentation.</p>
                        </div>
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.featured_products?.title || ''}
                                    onChange={(value) => handleInputChange('content.featured_products.title', value)}
                                    placeholder="e.g., Featured"
                                    required={true}
                                />
                                <ModernInput
                                    label="Items Count Text"
                                    value={formData.content?.featured_products?.items_count || ''}
                                    onChange={(value) => handleInputChange('content.featured_products.items_count', value)}
                                    placeholder="e.g., 24 items"
                                />
                            </div>
                            <div className="bg-gray-50 p-6 rounded-lg">
                                <h4 className="text-xl font-semibold mb-6">Product Grid (8 Products)</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                        <div key={num} className="bg-white p-4 rounded-lg shadow-sm">
                                            <h5 className="font-medium mb-3">Product {num}</h5>
                                            <div className="space-y-3">
                                                <ModernInput
                                                    label="Product Name"
                                                    value={formData.content?.featured_products?.[`product${num}_name`] || ''}
                                                    onChange={(value) => handleInputChange(`content.featured_products.product${num}_name`, value)}
                                                    placeholder={`Essential Item ${num}`}
                                                />
                                                <ModernInput
                                                    label="Price"
                                                    value={formData.content?.featured_products?.[`product${num}_price`] || ''}
                                                    onChange={(value) => handleInputChange(`content.featured_products.product${num}_price`, value)}
                                                    placeholder={`$${49 + num * 10}`}
                                                />
                                                <FileUploadArea
                                                    fieldName={`featured_product${num}_image`}
                                                    label="Product Image"
                                                    dimensions="400x500px"
                                                    description="Product photo"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            // Template 19 - BROADCAST (Fashion Editorial)
            case 'featured_stories':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <BookOpen className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Featured Stories</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your editorial content with compelling stories and articles.</p>
                        </div>
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.featured_stories?.title || ''}
                                    onChange={(value) => handleInputChange('content.featured_stories.title', value)}
                                    placeholder="e.g., Featured Stories"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Section Description"
                                    value={formData.content?.featured_stories?.description || ''}
                                    onChange={(value) => handleInputChange('content.featured_stories.description', value)}
                                    placeholder="Brief description of your stories section..."
                                    rows={2}
                                />
                            </div>
                            <div className="bg-emerald-50 p-6 rounded-lg">
                                <h4 className="text-xl font-semibold mb-6">Stories (3 Articles)</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {[1, 2, 3].map((num) => (
                                        <div key={num} className="bg-white p-6 rounded-lg">
                                            <h5 className="font-medium mb-4">Story {num}</h5>
                                            <div className="space-y-4">
                                                <ModernInput
                                                    label="Category"
                                                    value={formData.content?.featured_stories?.[`story${num}_category`] || ''}
                                                    onChange={(value) => handleInputChange(`content.featured_stories.story${num}_category`, value)}
                                                    placeholder={['FASHION', 'LIFESTYLE', 'CULTURE'][num - 1]}
                                                />
                                                <ModernInput
                                                    label="Story Title"
                                                    value={formData.content?.featured_stories?.[`story${num}_title`] || ''}
                                                    onChange={(value) => handleInputChange(`content.featured_stories.story${num}_title`, value)}
                                                    placeholder="Story headline..."
                                                />
                                                <ModernTextarea
                                                    label="Description"
                                                    value={formData.content?.featured_stories?.[`story${num}_description`] || ''}
                                                    onChange={(value) => handleInputChange(`content.featured_stories.story${num}_description`, value)}
                                                    placeholder="Story excerpt..."
                                                    rows={3}
                                                />
                                                <ModernInput
                                                    label="Read Time"
                                                    value={formData.content?.featured_stories?.[`story${num}_read_time`] || ''}
                                                    onChange={(value) => handleInputChange(`content.featured_stories.story${num}_read_time`, value)}
                                                    placeholder="5 min read"
                                                />
                                                <FileUploadArea
                                                    fieldName={`featured_story${num}_image`}
                                                    label="Story Image"
                                                    dimensions="600x400px"
                                                    description="Story cover image"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'editors_picks':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Star className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Editor's Picks</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Curate your best products with editorial-style presentation.</p>
                        </div>
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.editors_picks?.title || ''}
                                    onChange={(value) => handleInputChange('content.editors_picks.title', value)}
                                    placeholder="e.g., Editor's Picks"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Section Description"
                                    value={formData.content?.editors_picks?.description || ''}
                                    onChange={(value) => handleInputChange('content.editors_picks.description', value)}
                                    placeholder="Curated selections from our editorial team..."
                                    rows={2}
                                />
                            </div>
                            <div className="bg-emerald-50 p-6 rounded-lg">
                                <h4 className="text-xl font-semibold mb-6">Editor's Picks (8 Products)</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                        <div key={num} className="bg-white p-4 rounded-lg">
                                            <h5 className="font-medium mb-3">Pick {num}</h5>
                                            <div className="space-y-3">
                                                <ModernInput
                                                    label="Product Name"
                                                    value={formData.content?.editors_picks?.[`pick${num}_name`] || ''}
                                                    onChange={(value) => handleInputChange(`content.editors_picks.pick${num}_name`, value)}
                                                    placeholder={`Featured Piece ${num}`}
                                                />
                                                <ModernInput
                                                    label="Price"
                                                    value={formData.content?.editors_picks?.[`pick${num}_price`] || ''}
                                                    onChange={(value) => handleInputChange(`content.editors_picks.pick${num}_price`, value)}
                                                    placeholder={`$${129 + num * 25}`}
                                                />
                                                <FileUploadArea
                                                    fieldName={`editors_pick${num}_image`}
                                                    label="Product Image"
                                                    dimensions="400x500px"
                                                    description="Editorial pick image"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'quote_section':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-emerald-800 to-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Quote className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Inspirational Quote</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Feature a powerful quote that embodies your brand's philosophy.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernTextarea
                                    label="Quote Text"
                                    value={formData.content?.quote_section?.quote || ''}
                                    onChange={(value) => handleInputChange('content.quote_section.quote', value)}
                                    placeholder="Fashion is not just about clothing. It's about expressing who you are and who you want to become."
                                    rows={4}
                                    required={true}
                                />
                                <ModernInput
                                    label="Author/Source"
                                    value={formData.content?.quote_section?.author || ''}
                                    onChange={(value) => handleInputChange('content.quote_section.author', value)}
                                    placeholder="e.g., Editorial Director, BROADCAST"
                                    required={true}
                                />
                                <ModernInput
                                    label="Background Color Theme"
                                    value={formData.content?.quote_section?.background_theme || ''}
                                    onChange={(value) => handleInputChange('content.quote_section.background_theme', value)}
                                    placeholder="e.g., emerald, dark, gradient"
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="quote_background_image"
                                    label="Background Image (Optional)"
                                    dimensions="1920x600px"
                                    description="Background image for the quote section"
                                />
                            </div>
                        </div>
                    </div>
                );

            // Template 20 - STUDIO (Creative/Artist Portfolio)
            case 'portfolio_gallery':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Camera className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Portfolio Gallery</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your creative work in a beautiful masonry-style gallery.</p>
                        </div>
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.portfolio_gallery?.title || ''}
                                    onChange={(value) => handleInputChange('content.portfolio_gallery.title', value)}
                                    placeholder="e.g., Portfolio"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Section Description"
                                    value={formData.content?.portfolio_gallery?.description || ''}
                                    onChange={(value) => handleInputChange('content.portfolio_gallery.description', value)}
                                    placeholder="Brief description of your portfolio..."
                                    rows={2}
                                />
                            </div>
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-lg">
                                <h4 className="text-xl font-semibold mb-6">Gallery Items (8 Pieces)</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                        <div key={num} className="bg-white p-4 rounded-lg">
                                            <h5 className="font-medium mb-3">Piece {num}</h5>
                                            <div className="space-y-3">
                                                <ModernInput
                                                    label="Piece Title"
                                                    value={formData.content?.portfolio_gallery?.[`piece${num}_title`] || ''}
                                                    onChange={(value) => handleInputChange(`content.portfolio_gallery.piece${num}_title`, value)}
                                                    placeholder={`Piece #${num}`}
                                                />
                                                <ModernInput
                                                    label="Category"
                                                    value={formData.content?.portfolio_gallery?.[`piece${num}_category`] || ''}
                                                    onChange={(value) => handleInputChange(`content.portfolio_gallery.piece${num}_category`, value)}
                                                    placeholder="e.g., Limited Edition"
                                                />
                                                <ModernSelect
                                                    label="Aspect Ratio"
                                                    value={formData.content?.portfolio_gallery?.[`piece${num}_aspect`] || ''}
                                                    onChange={(value) => handleInputChange(`content.portfolio_gallery.piece${num}_aspect`, value)}
                                                    options={[
                                                        { value: '3/4', label: 'Portrait (3:4)' },
                                                        { value: 'square', label: 'Square (1:1)' },
                                                        { value: '4/5', label: 'Portrait (4:5)' }
                                                    ]}
                                                />
                                                <FileUploadArea
                                                    fieldName={`portfolio_piece${num}_image`}
                                                    label="Piece Image"
                                                    dimensions="400x500px"
                                                    description="Portfolio piece image"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'artist_statement':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Palette className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Artist Statement</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Share your artistic vision and philosophy with powerful storytelling.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Statement Title"
                                    value={formData.content?.artist_statement?.title || ''}
                                    onChange={(value) => handleInputChange('content.artist_statement.title', value)}
                                    placeholder="e.g., Art in Motion"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Artist Statement"
                                    value={formData.content?.artist_statement?.statement || ''}
                                    onChange={(value) => handleInputChange('content.artist_statement.statement', value)}
                                    placeholder="Every piece I create is a reflection of emotions, experiences, and dreams. Fashion is my canvas, and each design tells a unique story of creativity and passion."
                                    rows={6}
                                    required={true}
                                />
                                <ModernInput
                                    label="Artist Name/Title"
                                    value={formData.content?.artist_statement?.artist_title || ''}
                                    onChange={(value) => handleInputChange('content.artist_statement.artist_title', value)}
                                    placeholder="e.g., Creative Director, Studio"
                                    required={true}
                                />
                                <ModernInput
                                    label="Color Scheme"
                                    value={formData.content?.artist_statement?.color_scheme || ''}
                                    onChange={(value) => handleInputChange('content.artist_statement.color_scheme', value)}
                                    placeholder="e.g., orange-amber gradient"
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="artist_statement_background"
                                    label="Statement Background"
                                    dimensions="1920x800px"
                                    description="Background image for the artist statement section"
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'studio_visit':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <MapPin className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Studio Visit</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Invite visitors to experience your creative space and process.</p>
                        </div>
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <ModernInput
                                        label="Section Title"
                                        value={formData.content?.studio_visit?.title || ''}
                                        onChange={(value) => handleInputChange('content.studio_visit.title', value)}
                                        placeholder="e.g., Visit Our Studio"
                                        required={true}
                                    />
                                    <ModernTextarea
                                        label="Description"
                                        value={formData.content?.studio_visit?.description || ''}
                                        onChange={(value) => handleInputChange('content.studio_visit.description', value)}
                                        placeholder="Experience our creative process firsthand. Schedule a visit to see art in the making."
                                        rows={3}
                                        required={true}
                                    />
                                    <ModernInput
                                        label="Call-to-Action Text"
                                        value={formData.content?.studio_visit?.cta_text || ''}
                                        onChange={(value) => handleInputChange('content.studio_visit.cta_text', value)}
                                        placeholder="e.g., Book Studio Visit"
                                        required={true}
                                    />
                                    <ModernInput
                                        label="Location/Address"
                                        value={formData.content?.studio_visit?.location || ''}
                                        onChange={(value) => handleInputChange('content.studio_visit.location', value)}
                                        placeholder="e.g., Mumbai, India"
                                    />
                                    <ModernInput
                                        label="Contact Information"
                                        value={formData.content?.studio_visit?.contact_info || ''}
                                        onChange={(value) => handleInputChange('content.studio_visit.contact_info', value)}
                                        placeholder="e.g., studio@example.com or +91 12345 67890"
                                    />
                                </div>
                                <div>
                                    <FileUploadArea
                                        fieldName="studio_visit_image"
                                        label="Studio Image"
                                        dimensions="800x600px"
                                        description="Image of your studio or workspace"
                                    />
                                </div>
                            </div>
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-lg">
                                <h4 className="text-lg font-semibold mb-4">Collections Featured in Studio</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {[1, 2, 3].map((num) => (
                                        <div key={num} className="bg-white p-4 rounded-lg">
                                            <h5 className="font-medium mb-3">Collection {num}</h5>
                                            <div className="space-y-3">
                                                <ModernInput
                                                    label="Collection Name"
                                                    value={formData.content?.studio_visit?.[`collection${num}_name`] || ''}
                                                    onChange={(value) => handleInputChange(`content.studio_visit.collection${num}_name`, value)}
                                                    placeholder={['Abstract', 'Nature', 'Urban'][num - 1]}
                                                />
                                                <ModernInput
                                                    label="Theme"
                                                    value={formData.content?.studio_visit?.[`collection${num}_theme`] || ''}
                                                    onChange={(value) => handleInputChange(`content.studio_visit.collection${num}_theme`, value)}
                                                    placeholder={['Bold geometric patterns', 'Organic flowing designs', 'City-inspired aesthetics'][num - 1]}
                                                />
                                                <ModernInput
                                                    label="Pieces Count"
                                                    value={formData.content?.studio_visit?.[`collection${num}_count`] || ''}
                                                    onChange={(value) => handleInputChange(`content.studio_visit.collection${num}_count`, value)}
                                                    placeholder={[12, 8, 15][num - 1]}
                                                />
                                                <FileUploadArea
                                                    fieldName={`studio_collection${num}_image`}
                                                    label="Collection Image"
                                                    dimensions="400x400px"
                                                    description="Collection preview"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'luxury_statement':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-600 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Star className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Luxury Statement</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your heritage and luxury brand philosophy.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Statement Title"
                                    value={formData.content?.luxury_statement?.title || ''}
                                    onChange={(value) => handleInputChange('content.luxury_statement.title', value)}
                                    placeholder="e.g., Heritage of Excellence"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Heritage Description"
                                    value={formData.content?.luxury_statement?.description || ''}
                                    onChange={(value) => handleInputChange('content.luxury_statement.description', value)}
                                    placeholder="Tell your heritage story and brand philosophy..."
                                    rows={4}
                                    required={true}
                                />
                                <ModernInput
                                    label="Established Year"
                                    value={formData.content?.luxury_statement?.established_year || ''}
                                    onChange={(value) => handleInputChange('content.luxury_statement.established_year', value)}
                                    placeholder="e.g., EST. 1987"
                                />
                                <ModernTextarea
                                    label="Quality Statement"
                                    value={formData.content?.luxury_statement?.quality_statement || ''}
                                    onChange={(value) => handleInputChange('content.luxury_statement.quality_statement', value)}
                                    placeholder="Statement about your quality and craftsmanship..."
                                    rows={3}
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="luxury_statement_background"
                                    label="Background Image"
                                    dimensions="1200x800px"
                                    description="Luxury heritage background image"
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'video_story':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Video className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Video Story</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Share your brand story through compelling video content.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.video_story?.title || ''}
                                    onChange={(value) => handleInputChange('content.video_story.title', value)}
                                    placeholder="e.g., Our Story"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Video Description"
                                    value={formData.content?.video_story?.description || ''}
                                    onChange={(value) => handleInputChange('content.video_story.description', value)}
                                    placeholder="Describe what the video shows..."
                                    rows={3}
                                />
                                <ModernInput
                                    label="Video URL (YouTube/Vimeo)"
                                    value={formData.content?.video_story?.video_url || ''}
                                    onChange={(value) => handleInputChange('content.video_story.video_url', value)}
                                    placeholder="e.g., https://youtube.com/watch?v=..."
                                />
                                <ModernInput
                                    label="Video Duration (optional)"
                                    value={formData.content?.video_story?.duration || ''}
                                    onChange={(value) => handleInputChange('content.video_story.duration', value)}
                                    placeholder="e.g., 2:30"
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="video_story_thumbnail"
                                    label="Video Thumbnail"
                                    dimensions="1200x675px"
                                    description="Video cover image/thumbnail"
                                />
                            </div>
                        </div>
                    </div>
                );

            case 'newsletter':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Mail className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Newsletter Signup</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Encourage visitors to subscribe to your newsletter.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Section Title"
                                    value={formData.content?.newsletter?.title || ''}
                                    onChange={(value) => handleInputChange('content.newsletter.title', value)}
                                    placeholder="e.g., Stay connected"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.newsletter?.description || ''}
                                    onChange={(value) => handleInputChange('content.newsletter.description', value)}
                                    placeholder="Tell visitors about your newsletter benefits..."
                                    rows={3}
                                    required={true}
                                />
                                <ModernInput
                                    label="Button Text"
                                    value={formData.content?.newsletter?.button_text || ''}
                                    onChange={(value) => handleInputChange('content.newsletter.button_text', value)}
                                    placeholder="e.g., Subscribe, Join, Sign Up"
                                />
                                <ModernInput
                                    label="Placeholder Text"
                                    value={formData.content?.newsletter?.placeholder_text || ''}
                                    onChange={(value) => handleInputChange('content.newsletter.placeholder_text', value)}
                                    placeholder="e.g., Your email address"
                                />
                                <ModernTextarea
                                    label="Privacy Note (optional)"
                                    value={formData.content?.newsletter?.privacy_note || ''}
                                    onChange={(value) => handleInputChange('content.newsletter.privacy_note', value)}
                                    placeholder="e.g., We respect your privacy. Unsubscribe at any time."
                                    rows={2}
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="newsletter_background"
                                    label="Background Image (Optional)"
                                    dimensions="1200x600px"
                                    description="Background image for newsletter section"
                                />
                            </div>
                        </div>
                    </div>
                );

            // Template 15 (Minimalist) sections
            case 'collections_showcase':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-gray-600 to-slate-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Grid className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Collections Showcase</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Display your collections with elegant category navigation.</p>
                        </div>
                        <div className="space-y-6">
                            <ModernInput
                                label="Section Title"
                                value={formData.content?.collections_showcase?.title || ''}
                                onChange={(value) => handleInputChange('content.collections_showcase.title', value)}
                                placeholder="e.g., Collections"
                                required={true}
                            />
                            <ModernTextarea
                                label="Description"
                                value={formData.content?.collections_showcase?.description || ''}
                                onChange={(value) => handleInputChange('content.collections_showcase.description', value)}
                                placeholder="Describe your collections..."
                                rows={3}
                            />
                            <div className="space-y-4">
                                <h4 className="text-lg font-semibold text-gray-900">Categories</h4>
                                {[1, 2, 3].map((num) => (
                                    <div key={num} className="bg-gray-50 p-6 rounded-lg">
                                        <h5 className="text-md font-semibold mb-4">Category {num}</h5>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                            <div className="space-y-4">
                                                <ModernInput
                                                    label="Category Title"
                                                    value={formData.content?.collections_showcase?.[`category${num}_title`] || ''}
                                                    onChange={(value) => handleInputChange(`content.collections_showcase.category${num}_title`, value)}
                                                    placeholder="e.g., Women"
                                                />
                                                <ModernInput
                                                    label="Item Count"
                                                    value={formData.content?.collections_showcase?.[`category${num}_count`] || ''}
                                                    onChange={(value) => handleInputChange(`content.collections_showcase.category${num}_count`, value)}
                                                    placeholder="e.g., 250 pieces"
                                                />
                                            </div>
                                            <div>
                                                <FileUploadArea
                                                    fieldName={`collections_category${num}_image`}
                                                    label="Category Image"
                                                    dimensions="600x800px"
                                                    description="Category showcase image"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'brand_values':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Heart className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Brand Values</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your brand values with meaningful statistics.</p>
                        </div>
                        <div className="space-y-6">
                            <ModernInput
                                label="Section Title"
                                value={formData.content?.brand_values?.title || ''}
                                onChange={(value) => handleInputChange('content.brand_values.title', value)}
                                placeholder="e.g., Crafted with intention"
                                required={true}
                            />
                            <ModernTextarea
                                label="Description"
                                value={formData.content?.brand_values?.description || ''}
                                onChange={(value) => handleInputChange('content.brand_values.description', value)}
                                placeholder="Describe your brand values and commitment..."
                                rows={4}
                                required={true}
                            />
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {[1, 2, 3].map((num) => (
                                    <div key={num} className="bg-gray-50 p-6 rounded-lg">
                                        <h5 className="text-md font-semibold mb-4">Value {num}</h5>
                                        <div className="space-y-4">
                                            <ModernInput
                                                label="Label"
                                                value={formData.content?.brand_values?.[`value${num}_label`] || ''}
                                                onChange={(value) => handleInputChange(`content.brand_values.value${num}_label`, value)}
                                                placeholder="e.g., Sustainable"
                                                required={num <= 2}
                                            />
                                            <ModernInput
                                                label="Value"
                                                value={formData.content?.brand_values?.[`value${num}_value`] || ''}
                                                onChange={(value) => handleInputChange(`content.brand_values.value${num}_value`, value)}
                                                placeholder="e.g., 100%"
                                                required={num <= 2}
                                            />
                                            <ModernTextarea
                                                label="Description"
                                                value={formData.content?.brand_values?.[`value${num}_description`] || ''}
                                                onChange={(value) => handleInputChange(`content.brand_values.value${num}_description`, value)}
                                                placeholder="Brief description of this value..."
                                                rows={2}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'video_section':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <PlayCircle className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Our Story Video</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your brand story through compelling video content.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Video Title"
                                    value={formData.content?.video_section?.title || ''}
                                    onChange={(value) => handleInputChange('content.video_section.title', value)}
                                    placeholder="e.g., Our Story"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Video Description"
                                    value={formData.content?.video_section?.description || ''}
                                    onChange={(value) => handleInputChange('content.video_section.description', value)}
                                    placeholder="Brief description of the video content..."
                                    rows={3}
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="video_section_thumbnail"
                                    label="Video Thumbnail"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    dimensions="1200x675px"
                                    description="Thumbnail image for the video section (JPG, PNG, WebP only)"
                                />
                            </div>
                        </div>
                    </div>
                );

            // Template 16 (Prestige) sections
            case 'heritage_statement':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Star className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Heritage Statement</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Share your brand's heritage and legacy of excellence.</p>
                        </div>
                        <div className="space-y-6">
                            <ModernInput
                                label="Statement Title"
                                value={formData.content?.heritage_statement?.title || ''}
                                onChange={(value) => handleInputChange('content.heritage_statement.title', value)}
                                placeholder="e.g., Heritage of Excellence"
                                required={true}
                            />
                            <ModernTextarea
                                label="Heritage Description"
                                value={formData.content?.heritage_statement?.description || ''}
                                onChange={(value) => handleInputChange('content.heritage_statement.description', value)}
                                placeholder="Tell the story of your brand's heritage, craftsmanship, and commitment to excellence..."
                                rows={5}
                                required={true}
                            />
                        </div>
                    </div>
                );
            case 'premium_services':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Settings className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Premium Services</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase exclusive services designed for your valued clientele.</p>
                        </div>
                        <div className="space-y-6">
                            <ModernInput
                                label="Section Title"
                                value={formData.content?.premium_services?.title || ''}
                                onChange={(value) => handleInputChange('content.premium_services.title', value)}
                                placeholder="e.g., Prestige Services"
                                required={true}
                            />
                            <ModernTextarea
                                label="Description"
                                value={formData.content?.premium_services?.description || ''}
                                onChange={(value) => handleInputChange('content.premium_services.description', value)}
                                placeholder="Describe your exclusive services for valued clients..."
                                rows={3}
                                required={true}
                            />
                            <div className="space-y-4">
                                <h4 className="text-lg font-semibold text-gray-900">Services</h4>
                                {[1, 2, 3].map((num) => (
                                    <div key={num} className="bg-gray-50 p-6 rounded-lg">
                                        <h5 className="text-md font-semibold mb-4">Service {num}</h5>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                            <ModernInput
                                                label="Service Title"
                                                value={formData.content?.premium_services?.[`service${num}_title`] || ''}
                                                onChange={(value) => handleInputChange(`content.premium_services.service${num}_title`, value)}
                                                placeholder="e.g., Personal Styling"
                                                required={num <= 2}
                                            />
                                            <ModernTextarea
                                                label="Service Description"
                                                value={formData.content?.premium_services?.[`service${num}_description`] || ''}
                                                onChange={(value) => handleInputChange(`content.premium_services.service${num}_description`, value)}
                                                placeholder="Brief description of the service..."
                                                rows={3}
                                                required={num <= 2}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            // Template 17 (Nordic) sections
            case 'hero_with_values':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Leaf className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Hero with Values</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Create an engaging hero section that showcases your values.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Main Title"
                                    value={formData.content?.hero_with_values?.title || ''}
                                    onChange={(value) => handleInputChange('content.hero_with_values.title', value)}
                                    placeholder="e.g., Natural beauty, thoughtful design"
                                    required={true}
                                />
                                <ModernInput
                                    label="Subtitle"
                                    value={formData.content?.hero_with_values?.subtitle || ''}
                                    onChange={(value) => handleInputChange('content.hero_with_values.subtitle', value)}
                                    placeholder="e.g., Sustainably crafted"
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.hero_with_values?.description || ''}
                                    onChange={(value) => handleInputChange('content.hero_with_values.description', value)}
                                    placeholder="Describe your brand values and mission..."
                                    rows={4}
                                    required={true}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <ModernInput
                                        label="Button 1 Text"
                                        value={formData.content?.hero_with_values?.button1_text || ''}
                                        onChange={(value) => handleInputChange('content.hero_with_values.button1_text', value)}
                                        placeholder="e.g., Explore Collection"
                                    />
                                    <ModernInput
                                        label="Button 2 Text"
                                        value={formData.content?.hero_with_values?.button2_text || ''}
                                        onChange={(value) => handleInputChange('content.hero_with_values.button2_text', value)}
                                        placeholder="e.g., Our Story"
                                    />
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    <h4 className="lg:col-span-3 text-lg font-semibold text-gray-900">Values</h4>
                                    {[1, 2, 3].map((num) => (
                                        <div key={num} className="bg-gray-50 p-4 rounded-lg">
                                            <h5 className="text-sm font-semibold mb-3">Value {num}</h5>
                                            <div className="space-y-3">
                                                <ModernInput
                                                    label="Label"
                                                    value={formData.content?.hero_with_values?.[`value${num}_label`] || ''}
                                                    onChange={(value) => handleInputChange(`content.hero_with_values.value${num}_label`, value)}
                                                    placeholder="e.g., Sustainable"
                                                    required={num <= 2}
                                                />
                                                <ModernTextarea
                                                    label="Description"
                                                    value={formData.content?.hero_with_values?.[`value${num}_description`] || ''}
                                                    onChange={(value) => handleInputChange(`content.hero_with_values.value${num}_description`, value)}
                                                    placeholder="e.g., 100% ethical"
                                                    rows={2}
                                                    required={num <= 2}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="hero_with_values_image"
                                    label="Hero Image"
                                    dimensions="800x600px"
                                    description="Main hero section image"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'category_showcase':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Grid className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Category Showcase</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Display your product categories with thoughtful curation.</p>
                        </div>
                        <div className="space-y-6">
                            <ModernInput
                                label="Section Title"
                                value={formData.content?.category_showcase?.title || ''}
                                onChange={(value) => handleInputChange('content.category_showcase.title', value)}
                                placeholder="e.g., Thoughtfully curated"
                                required={true}
                            />
                            <ModernTextarea
                                label="Description"
                                value={formData.content?.category_showcase?.description || ''}
                                onChange={(value) => handleInputChange('content.category_showcase.description', value)}
                                placeholder="Describe your curation approach..."
                                rows={3}
                                required={true}
                            />
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {[1, 2, 3, 4].map((num) => (
                                    <div key={num} className="bg-gray-50 p-6 rounded-lg">
                                        <h5 className="text-md font-semibold mb-4">Category {num}</h5>
                                        <div className="space-y-4">
                                            <ModernInput
                                                label="Category Title"
                                                value={formData.content?.category_showcase?.[`category${num}_title`] || ''}
                                                onChange={(value) => handleInputChange(`content.category_showcase.category${num}_title`, value)}
                                                placeholder="e.g., Women"
                                                required={num <= 3}
                                            />
                                            <ModernInput
                                                label="Item Count"
                                                value={formData.content?.category_showcase?.[`category${num}_count`] || ''}
                                                onChange={(value) => handleInputChange(`content.category_showcase.category${num}_count`, value)}
                                                placeholder="e.g., 280+ pieces"
                                                required={num <= 3}
                                            />
                                            <FileUploadArea
                                                fieldName={`category_showcase_image${num}`}
                                                label="Category Image"
                                                dimensions="500x600px"
                                                description="Category image"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'craftsmanship_story':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Settings className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Craftsmanship Story</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Share your craftsmanship journey with meaningful statistics.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Story Title"
                                    value={formData.content?.craftsmanship_story?.title || ''}
                                    onChange={(value) => handleInputChange('content.craftsmanship_story.title', value)}
                                    placeholder="e.g., Handcrafted with purpose"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.craftsmanship_story?.description || ''}
                                    onChange={(value) => handleInputChange('content.craftsmanship_story.description', value)}
                                    placeholder="Tell your craftsmanship story..."
                                    rows={4}
                                    required={true}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <h4 className="col-span-2 text-lg font-semibold text-gray-900">Statistics</h4>
                                    {[1, 2, 3, 4].map((num) => (
                                        <React.Fragment key={num}>
                                            <ModernInput
                                                label={`Stat ${num} Value`}
                                                value={formData.content?.craftsmanship_story?.[`stat${num}_value`] || ''}
                                                onChange={(value) => handleInputChange(`content.craftsmanship_story.stat${num}_value`, value)}
                                                placeholder="e.g., 15+"
                                                required={num <= 3}
                                            />
                                            <ModernInput
                                                label={`Stat ${num} Label`}
                                                value={formData.content?.craftsmanship_story?.[`stat${num}_label`] || ''}
                                                onChange={(value) => handleInputChange(`content.craftsmanship_story.stat${num}_label`, value)}
                                                placeholder="e.g., Years of craftsmanship"
                                                required={num <= 3}
                                            />
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="craftsmanship_story_image"
                                    label="Craftsmanship Image"
                                    dimensions="700x500px"
                                    description="Image showcasing your craftsmanship"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'video_story':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <PlayCircle className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Video Story</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Share your story through compelling video content.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Video Title"
                                    value={formData.content?.video_story?.title || ''}
                                    onChange={(value) => handleInputChange('content.video_story.title', value)}
                                    placeholder="e.g., From our workshop to your home"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.video_story?.description || ''}
                                    onChange={(value) => handleInputChange('content.video_story.description', value)}
                                    placeholder="Describe your video story..."
                                    rows={4}
                                    required={true}
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="video_story_thumbnail"
                                    label="Video Thumbnail"
                                    dimensions="1000x600px"
                                    description="Thumbnail for your video story"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'values_principles':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Quote className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Values & Principles</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Share your core values and guiding principles.</p>
                        </div>
                        <div className="space-y-8">
                            <div className="bg-gray-50 p-8 rounded-lg text-center">
                                <h4 className="text-lg font-semibold text-gray-900 mb-6">Inspirational Quote</h4>
                                <ModernTextarea
                                    label="Quote Text"
                                    value={formData.content?.values_principles?.quote || ''}
                                    onChange={(value) => handleInputChange('content.values_principles.quote', value)}
                                    placeholder="e.g., We design for real life, real people, and real moments"
                                    rows={3}
                                    required={true}
                                />
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <ModernInput
                                        label="Author Name"
                                        value={formData.content?.values_principles?.author_name || ''}
                                        onChange={(value) => handleInputChange('content.values_principles.author_name', value)}
                                        placeholder="e.g., Erik Lundberg"
                                        required={true}
                                    />
                                    <ModernInput
                                        label="Author Title"
                                        value={formData.content?.values_principles?.author_title || ''}
                                        onChange={(value) => handleInputChange('content.values_principles.author_title', value)}
                                        placeholder="e.g., Creative Director"
                                        required={true}
                                    />
                                </div>
                            </div>
                            <div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-6">Core Principles</h4>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {[1, 2, 3].map((num) => (
                                        <div key={num} className="bg-gray-50 p-6 rounded-lg">
                                            <h5 className="text-md font-semibold mb-4">Principle {num}</h5>
                                            <div className="space-y-4">
                                                <ModernInput
                                                    label="Title"
                                                    value={formData.content?.values_principles?.[`principle${num}_title`] || ''}
                                                    onChange={(value) => handleInputChange(`content.values_principles.principle${num}_title`, value)}
                                                    placeholder="e.g., Local Sourcing"
                                                    required={true}
                                                />
                                                <ModernTextarea
                                                    label="Description"
                                                    value={formData.content?.values_principles?.[`principle${num}_description`] || ''}
                                                    onChange={(value) => handleInputChange(`content.values_principles.principle${num}_description`, value)}
                                                    placeholder="Describe this principle..."
                                                    rows={3}
                                                    required={true}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            // Template 18 (Focal) sections
            case 'category_grid':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Grid className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Category Grid</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Display your product categories in a clean, organized grid.</p>
                        </div>
                        <div className="space-y-6">
                            <ModernInput
                                label="Section Title"
                                value={formData.content?.category_grid?.title || ''}
                                onChange={(value) => handleInputChange('content.category_grid.title', value)}
                                placeholder="e.g., Shop by Category"
                                required={true}
                            />
                            <div className="space-y-4">
                                <h4 className="text-lg font-semibold text-gray-900">Categories</h4>
                                {[1, 2, 3].map((num) => (
                                    <div key={num} className="bg-gray-50 p-6 rounded-lg">
                                        <h5 className="text-md font-semibold mb-4">Category {num}</h5>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                            <div className="space-y-4">
                                                <ModernInput
                                                    label="Category Name"
                                                    value={formData.content?.category_grid?.[`category${num}_name`] || ''}
                                                    onChange={(value) => handleInputChange(`content.category_grid.category${num}_name`, value)}
                                                    placeholder="e.g., Essentials"
                                                    required={true}
                                                />
                                            </div>
                                            <div>
                                                <FileUploadArea
                                                    fieldName={`category_grid_image${num}`}
                                                    label="Category Image"
                                                    dimensions="600x400px"
                                                    description="Category image"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'quality_values':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Star className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Quality Values</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Highlight what makes your brand exceptional.</p>
                        </div>
                        <div className="space-y-6">
                            <ModernInput
                                label="Section Title"
                                value={formData.content?.quality_values?.title || ''}
                                onChange={(value) => handleInputChange('content.quality_values.title', value)}
                                placeholder="e.g., Quality is our focus"
                                required={true}
                            />
                            <ModernTextarea
                                label="Description"
                                value={formData.content?.quality_values?.description || ''}
                                onChange={(value) => handleInputChange('content.quality_values.description', value)}
                                placeholder="Describe your commitment to quality..."
                                rows={4}
                                required={true}
                            />
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {[1, 2, 3].map((num) => (
                                    <div key={num} className="bg-gray-50 p-6 rounded-lg text-center">
                                        <h5 className="text-md font-semibold mb-4">Value {num}</h5>
                                        <div className="space-y-4">
                                            <ModernInput
                                                label="Value Title"
                                                value={formData.content?.quality_values?.[`value${num}_title`] || ''}
                                                onChange={(value) => handleInputChange(`content.quality_values.value${num}_title`, value)}
                                                placeholder="e.g., Quality"
                                                required={true}
                                            />
                                            <ModernTextarea
                                                label="Description"
                                                value={formData.content?.quality_values?.[`value${num}_description`] || ''}
                                                onChange={(value) => handleInputChange(`content.quality_values.value${num}_description`, value)}
                                                placeholder="Describe this value..."
                                                rows={3}
                                                required={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            // Template 19 (Broadcast) sections
            case 'magazine_hero':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <BookOpen className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Magazine Hero</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Create an editorial-style hero section.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Issue Date/Text"
                                    value={formData.content?.magazine_hero?.issue_date || ''}
                                    onChange={(value) => handleInputChange('content.magazine_hero.issue_date', value)}
                                    placeholder="e.g., WINTER ISSUE 2025"
                                    required={true}
                                />
                                <ModernInput
                                    label="Main Title"
                                    value={formData.content?.magazine_hero?.title || ''}
                                    onChange={(value) => handleInputChange('content.magazine_hero.title', value)}
                                    placeholder="e.g., The Future of Fashion"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.magazine_hero?.description || ''}
                                    onChange={(value) => handleInputChange('content.magazine_hero.description', value)}
                                    placeholder="Describe the featured story..."
                                    rows={4}
                                    required={true}
                                />
                                <ModernInput
                                    label="Button Text"
                                    value={formData.content?.magazine_hero?.button_text || ''}
                                    onChange={(value) => handleInputChange('content.magazine_hero.button_text', value)}
                                    placeholder="e.g., READ THE STORY"
                                />
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="magazine_hero_image"
                                    label="Hero Image"
                                    dimensions="800x600px"
                                    description="Editorial hero image"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'editorial_stories':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FileText className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Editorial Stories</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Featured editorial content and articles.</p>
                        </div>
                        <div className="space-y-6">
                            <ModernInput
                                label="Section Title"
                                value={formData.content?.editorial_stories?.title || ''}
                                onChange={(value) => handleInputChange('content.editorial_stories.title', value)}
                                placeholder="e.g., Featured Stories"
                                required={true}
                            />
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {[1, 2, 3].map((num) => (
                                    <div key={num} className="bg-gray-50 p-6 rounded-lg">
                                        <h5 className="text-md font-semibold mb-4">Story {num}</h5>
                                        <div className="space-y-4">
                                            <ModernInput
                                                label="Category"
                                                value={formData.content?.editorial_stories?.[`story${num}_category`] || ''}
                                                onChange={(value) => handleInputChange(`content.editorial_stories.story${num}_category`, value)}
                                                placeholder="e.g., FASHION"
                                                required={num <= 2}
                                            />
                                            <ModernInput
                                                label="Story Title"
                                                value={formData.content?.editorial_stories?.[`story${num}_title`] || ''}
                                                onChange={(value) => handleInputChange(`content.editorial_stories.story${num}_title`, value)}
                                                placeholder="e.g., Sustainable Luxury in Modern Fashion"
                                                required={num <= 2}
                                            />
                                            <ModernTextarea
                                                label="Description"
                                                value={formData.content?.editorial_stories?.[`story${num}_description`] || ''}
                                                onChange={(value) => handleInputChange(`content.editorial_stories.story${num}_description`, value)}
                                                placeholder="Story description..."
                                                rows={3}
                                                required={num <= 2}
                                            />
                                            <ModernInput
                                                label="Read Time"
                                                value={formData.content?.editorial_stories?.[`story${num}_read_time`] || ''}
                                                onChange={(value) => handleInputChange(`content.editorial_stories.story${num}_read_time`, value)}
                                                placeholder="e.g., 5 min read"
                                            />
                                            <FileUploadArea
                                                fieldName={`editorial_story${num}_image`}
                                                label="Story Image"
                                                dimensions="600x400px"
                                                description="Editorial story image"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'inspirational_quote':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Quote className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Inspirational Quote</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Share an inspiring quote or message.</p>
                        </div>
                        <div className="max-w-2xl mx-auto space-y-6">
                            <ModernTextarea
                                label="Quote Text"
                                value={formData.content?.inspirational_quote?.quote_text || ''}
                                onChange={(value) => handleInputChange('content.inspirational_quote.quote_text', value)}
                                placeholder="e.g., Fashion is not just about clothing. It's about expressing who you are and who you want to become."
                                rows={4}
                                required={true}
                            />
                            <ModernInput
                                label="Author"
                                value={formData.content?.inspirational_quote?.author || ''}
                                onChange={(value) => handleInputChange('content.inspirational_quote.author', value)}
                                placeholder="e.g., Editorial Director, BROADCAST"
                                required={true}
                            />
                        </div>
                    </div>
                );

            // Template 20 (Studio) sections
            case 'artist_hero':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Palette className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Artist Hero</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Create an inspiring artist hero section.</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <ModernInput
                                    label="Main Title"
                                    value={formData.content?.artist_hero?.main_title || ''}
                                    onChange={(value) => handleInputChange('content.artist_hero.main_title', value)}
                                    placeholder="e.g., Creative"
                                    required={true}
                                />
                                <ModernInput
                                    label="Subtitle"
                                    value={formData.content?.artist_hero?.subtitle || ''}
                                    onChange={(value) => handleInputChange('content.artist_hero.subtitle', value)}
                                    placeholder="e.g., Expression"
                                    required={true}
                                />
                                <ModernTextarea
                                    label="Description"
                                    value={formData.content?.artist_hero?.description || ''}
                                    onChange={(value) => handleInputChange('content.artist_hero.description', value)}
                                    placeholder="Welcome to our creative studio..."
                                    rows={4}
                                    required={true}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <ModernInput
                                        label="Button 1 Text"
                                        value={formData.content?.artist_hero?.button1_text || ''}
                                        onChange={(value) => handleInputChange('content.artist_hero.button1_text', value)}
                                        placeholder="e.g., View Gallery"
                                    />
                                    <ModernInput
                                        label="Button 2 Text"
                                        value={formData.content?.artist_hero?.button2_text || ''}
                                        onChange={(value) => handleInputChange('content.artist_hero.button2_text', value)}
                                        placeholder="e.g., Artist Story"
                                    />
                                </div>
                            </div>
                            <div>
                                <FileUploadArea
                                    fieldName="artist_hero_image"
                                    label="Hero Image"
                                    dimensions="600x600px"
                                    description="Artist hero image"
                                />
                            </div>
                        </div>
                    </div>
                );
            case 'portfolio_gallery':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Camera className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Portfolio Gallery</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Showcase your artistic portfolio in a masonry-style grid.</p>
                        </div>
                        <div className="space-y-6">
                            <ModernInput
                                label="Gallery Title"
                                value={formData.content?.portfolio_gallery?.title || ''}
                                onChange={(value) => handleInputChange('content.portfolio_gallery.title', value)}
                                placeholder="e.g., Portfolio"
                                required={true}
                            />
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                    <div key={num} className="bg-gray-50 p-4 rounded-lg">
                                        <h5 className="text-sm font-semibold mb-3">Gallery Item {num}</h5>
                                        <div className="space-y-3">
                                            <ModernInput
                                                label="Title"
                                                value={formData.content?.portfolio_gallery?.[`item${num}_title`] || ''}
                                                onChange={(value) => handleInputChange(`content.portfolio_gallery.item${num}_title`, value)}
                                                placeholder={`e.g., Piece #${num}`}
                                                required={num <= 6}
                                            />
                                            <FileUploadArea
                                                fieldName={`portfolio_gallery_item${num}`}
                                                label="Gallery Image"
                                                dimensions="400x500px"
                                                description="Portfolio item image"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'artist_statement':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Quote className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Artist Statement</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Share your artistic vision and creative philosophy.</p>
                        </div>
                        <div className="max-w-2xl mx-auto space-y-6">
                            <ModernInput
                                label="Statement Title"
                                value={formData.content?.artist_statement?.title || ''}
                                onChange={(value) => handleInputChange('content.artist_statement.title', value)}
                                placeholder="e.g., Art in Motion"
                                required={true}
                            />
                            <ModernTextarea
                                label="Statement Text"
                                value={formData.content?.artist_statement?.statement_text || ''}
                                onChange={(value) => handleInputChange('content.artist_statement.statement_text', value)}
                                placeholder="Share your artistic philosophy..."
                                rows={6}
                                required={true}
                            />
                            <ModernInput
                                label="Author"
                                value={formData.content?.artist_statement?.author || ''}
                                onChange={(value) => handleInputChange('content.artist_statement.author', value)}
                                placeholder="e.g., Creative Director, Studio"
                                required={true}
                            />
                        </div>
                    </div>
                );
            case 'studio_contact':
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <MapPin className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-3">Studio Contact</h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">Invite visitors to experience your creative studio.</p>
                        </div>
                        <div className="max-w-2xl mx-auto space-y-6">
                            <ModernInput
                                label="Section Title"
                                value={formData.content?.studio_contact?.title || ''}
                                onChange={(value) => handleInputChange('content.studio_contact.title', value)}
                                placeholder="e.g., Visit Our Studio"
                                required={true}
                            />
                            <ModernTextarea
                                label="Description"
                                value={formData.content?.studio_contact?.description || ''}
                                onChange={(value) => handleInputChange('content.studio_contact.description', value)}
                                placeholder="Experience our creative process firsthand..."
                                rows={4}
                                required={true}
                            />
                            <ModernInput
                                label="Button Text"
                                value={formData.content?.studio_contact?.button_text || ''}
                                onChange={(value) => handleInputChange('content.studio_contact.button_text', value)}
                                placeholder="e.g., Book Studio Visit"
                                required={true}
                            />
                        </div>
                    </div>
                );

            default:
                return renderGenericArraySection(currentSection);
        }
    };

    // Enhanced submit handler with comprehensive validation
    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);

            // Comprehensive validation using the validation system
            const errors = {};

            // Validate all form data recursively
            const validateFormData = (data, basePath = '') => {
                if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
                    Object.keys(data).forEach(key => {
                        const fullPath = basePath ? `${basePath}.${key}` : key;
                        const value = data[key];

                        if (Array.isArray(value)) {
                            value.forEach((item, index) => {
                                validateFormData(item, `${fullPath}.${index}`);
                            });
                        } else if (typeof value === 'object' && value !== null) {
                            validateFormData(value, fullPath);
                        }
                    });
                }
            };

            // Core required field validation
            if (!formData.content?.brand?.name) {
                errors['content.brand.name'] = 'Brand name is required';
            }
            if (!formData.content?.brand?.description) {
                errors['content.brand.description'] = 'Brand description is required';
            }
            if (!uploadedFiles['content.brand.logo']) {
                errors['content.brand.logo'] = 'Brand logo is required';
            }

            // Validate business information if section exists
            if (selectedTemplate?.sections?.includes('business')) {
                if (!formData.content?.business?.contact?.business_name) {
                    errors['content.business.contact.business_name'] = 'Business name is required';
                }
                if (!formData.content?.business?.contact?.email) {
                    errors['content.business.contact.email'] = 'Business email is required';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.content.business.contact.email)) {
                    errors['content.business.contact.email'] = 'Invalid email format';
                }
                if (!formData.content?.business?.contact?.phone) {
                    errors['content.business.contact.phone'] = 'Business phone is required';
                }
            }

            // Validate categories if they exist
            if (formData.content?.categories?.length > 0) {
                formData.content.categories.forEach((category, index) => {
                    if (!category.name) {
                        errors[`content.categories.${index}.name`] = 'Category name is required';
                    }
                });
            }

            // Validate videos if they exist
            if (formData.content?.videos?.length > 0) {
                formData.content.videos.forEach((video, index) => {
                    if (!video.title) {
                        errors[`content.videos.${index}.title`] = 'Video title is required';
                    }
                    if (!video.url && !video.file) {
                        errors[`content.videos.${index}.url`] = 'Video URL or file is required';
                    }
                });
            }

            // Validate hero section if it exists
            if (selectedTemplate?.sections?.includes('hero')) {
                if (!formData.content?.hero?.title) {
                    errors['content.hero.title'] = 'Hero title is required';
                }
                if (!uploadedFiles['content.hero.background_image']) {
                    errors['content.hero.background_image'] = 'Hero background image is required';
                }
            }

            if (Object.keys(errors).length > 0) {
                setValidationErrors(errors);

                // Count total errors
                const errorCount = Object.keys(errors).length;
                const errorMessages = Object.values(errors).slice(0, 3); // Show first 3 errors

                Swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    html: `
                        <p class="mb-2">Please fix the following ${errorCount} error${errorCount > 1 ? 's' : ''}:</p>
                        <ul class="text-left text-sm">
                            ${errorMessages.map(msg => `<li>• ${msg}</li>`).join('')}
                            ${errorCount > 3 ? `<li>• ... and ${errorCount - 3} more</li>` : ''}
                        </ul>
                    `,
                    confirmButtonColor: '#f97316'
                });
                return;
            }

            // Clear any previous validation errors
            setValidationErrors({});

            // Debug: Log the current form data to see what's being captured
            console.log('🔍 Current formData state:', formData);
            console.log('🔍 Selected template:', selectedTemplate);
            console.log('🔍 Uploaded files:', uploadedFiles);

            // Helper function to remove file paths from content - let backend handle file URLs
            const cleanContentFromFiles = (content) => {
                if (!content) return content;

                const cleaned = JSON.parse(JSON.stringify(content)); // Deep clone

                // Remove file paths from specific fields
                const removeFileField = (obj, fieldName) => {
                    if (obj && typeof obj === 'object') {
                        if (obj[fieldName]) {
                            delete obj[fieldName]; // Remove file field, backend will set the URL
                        }
                    }
                };

                // Clean brand logo
                if (cleaned.brand) {
                    removeFileField(cleaned.brand, 'logo');
                }

                // Clean hero background image
                if (cleaned.hero) {
                    removeFileField(cleaned.hero, 'background_image');
                }

                // Clean categories images
                if (cleaned.categories && Array.isArray(cleaned.categories)) {
                    cleaned.categories.forEach(category => removeFileField(category, 'image'));
                }

                // Clean videos files
                if (cleaned.videos && Array.isArray(cleaned.videos)) {
                    cleaned.videos.forEach(video => removeFileField(video, 'file'));
                }

                // Clean array sections images
                ['sliding_banners', 'promo_banners', 'fashion_trends', 'banners', 'collections', 'gallery', 'instagram'].forEach(sectionName => {
                    if (cleaned[sectionName] && Array.isArray(cleaned[sectionName])) {
                        cleaned[sectionName].forEach(item => removeFileField(item, 'image'));
                    }
                });

                // Clean statement and workshop background images
                if (cleaned.statement) {
                    removeFileField(cleaned.statement, 'background_image');
                }
                if (cleaned.workshop) {
                    removeFileField(cleaned.workshop, 'image');
                }

                return cleaned;
            };

            // Create clean, validated JSON structure
            const cleanFormData = {
                template_id: selectedTemplate.id,
                theme: {
                    colors: formData.theme?.colors || {
                        primary: '#000000',
                        secondary: '#ffffff',
                        accent: '#f3f4f6',
                        background: '#ffffff',
                        text: '#111827'
                    },
                    fonts: {
                        heading: formData.theme?.fonts?.heading || 'Playfair Display',
                        body: formData.theme?.fonts?.body || 'Inter'
                    },
                    spacing: formData.theme?.spacing || 'comfortable',
                    borderRadius: formData.theme?.borderRadius || 'rounded',
                    shadows: formData.theme?.shadows || 'soft'
                },
                layout: {
                    sections: selectedTemplate?.sections || [],
                    heroStyle: formData.layout?.heroStyle || 'overlay',
                    categoryStyle: formData.layout?.categoryStyle || 'grid',
                    showcaseStyle: formData.layout?.showcaseStyle || 'featured',
                    productLayout: formData.layout?.productLayout || 'grid'
                },
                animations: {
                    enabled: formData.animations?.enabled !== false,
                    style: formData.animations?.style || 'smooth',
                    pageTransitions: formData.animations?.pageTransitions || 'fade',
                    hoverEffects: formData.animations?.hoverEffects || 'lift',
                    scrollAnimations: formData.animations?.scrollAnimations || 'parallax'
                },
                content: cleanContentFromFiles(formData.content || {})
            };

            // Content is already cleaned and included above - no manual building needed

            // Validate JSON structure before sending
            try {
                const jsonString = JSON.stringify(cleanFormData, null, 2);
                const parsedData = JSON.parse(jsonString);

                console.log('📋 Final JSON Structure:', parsedData);
                console.log('🎨 Colors being sent:', parsedData.theme?.colors);

                // Ensure all required fields are present
                if (!parsedData.template_id || !parsedData.content?.brand?.name) {
                    throw new Error('Invalid JSON structure - missing required fields');
                }

            } catch (jsonError) {
                console.error('JSON Validation Error:', jsonError);
                Swal.fire({
                    icon: 'error',
                    title: 'Data Structure Error',
                    text: 'Invalid data format. Please check your inputs and try again.',
                    confirmButtonColor: '#f97316'
                });
                return;
            }

            // Create FormData for submission
            const submitData = new FormData();

            // Add required template information that backend expects
            submitData.append('temp_no', selectedTemplate.id);
            submitData.append('temp_name', selectedTemplate.name);

            // Add the clean, validated template configuration
            submitData.append('template_config', JSON.stringify(cleanFormData));

            // Add files with proper backend field naming
            Object.keys(uploadedFiles).forEach(fieldPath => {
                if (uploadedFiles[fieldPath]) {
                    const backendFieldName = mapFrontendPathToBackendField(fieldPath);
                    submitData.append(backendFieldName, uploadedFiles[fieldPath]);
                    console.log(`📎 Adding file: ${backendFieldName} <- ${fieldPath}`, uploadedFiles[fieldPath].name);
                }
            });

            console.log('📤 Final FormData being sent:');
            for (let [key, value] of submitData.entries()) {
                if (value instanceof File) {
                    console.log(`  ${key}: [File] ${value.name} (${value.size} bytes)`);
                } else {
                    console.log(`  ${key}: ${value}`);
                }
            }

            // Helper function to map frontend field paths to backend field names
            function mapFrontendPathToBackendField(fieldPath) {
                // Brand logo: content.brand.logo -> logo
                if (fieldPath === 'content.brand.logo') return 'logo';

                // Hero background: content.hero.background_image -> hero_image
                if (fieldPath === 'content.hero.background_image') return 'hero_image';

                // Category images: content.categories.0.image -> category_image_0
                const categoryMatch = fieldPath.match(/^content\.categories\.(\d+)\.image$/);
                if (categoryMatch) return `category_image_${categoryMatch[1]}`;

                // Video files: content.videos.0.file -> video_file_0
                const videoMatch = fieldPath.match(/^content\.videos\.(\d+)\.file$/);
                if (videoMatch) return `video_file_${videoMatch[1]}`;

                // Sliding banner images: content.sliding_banners.0.image -> sliding_banner_image_0
                const slidingBannerMatch = fieldPath.match(/^content\.sliding_banners\.(\d+)\.image$/);
                if (slidingBannerMatch) return `sliding_banner_image_${slidingBannerMatch[1]}`;

                // Promo banner images: content.promo_banners.0.image -> promo_banner_image_0
                const promoBannerMatch = fieldPath.match(/^content\.promo_banners\.(\d+)\.image$/);
                if (promoBannerMatch) return `promo_banner_image_${promoBannerMatch[1]}`;

                // Fashion trend images: content.fashion_trends.0.image -> fashion_trend_image_0
                const fashionTrendMatch = fieldPath.match(/^content\.fashion_trends\.(\d+)\.image$/);
                if (fashionTrendMatch) return `fashion_trend_image_${fashionTrendMatch[1]}`;

                // Collection images: content.collections.0.image -> collection_image_0
                const collectionMatch = fieldPath.match(/^content\.collections\.(\d+)\.image$/);
                if (collectionMatch) return `collection_image_${collectionMatch[1]}`;

                // Gallery images: content.gallery.0.image -> gallery_image_1 (1-indexed for backend)
                const galleryMatch = fieldPath.match(/^content\.gallery\.(\d+)\.image$/);
                if (galleryMatch) return `gallery_image_${parseInt(galleryMatch[1]) + 1}`;

                // Instagram images: content.instagram.0.image -> instagram_image_1 (1-indexed for backend)
                const instagramMatch = fieldPath.match(/^content\.instagram\.(\d+)\.image$/);
                if (instagramMatch) return `instagram_image_${parseInt(instagramMatch[1]) + 1}`;

                // Banners: content.banners.0.image -> banner_image_0
                const bannerMatch = fieldPath.match(/^content\.banners\.(\d+)\.image$/);
                if (bannerMatch) return `banner_image_${bannerMatch[1]}`;

                // Statement background: content.statement.background_image -> statement_background_image
                if (fieldPath === 'content.statement.background_image') return 'statement_background_image';

                // Workshop image: content.workshop.image -> workshop_image
                if (fieldPath === 'content.workshop.image') return 'workshop_image';

                // ========== TEMPLATE 15-20 SPECIFIC SECTION IMAGES ==========

                // Template 15 - Collections Showcase category images
                if (fieldPath === 'collections_category1_image') return 'collections_category1_image';
                if (fieldPath === 'collections_category2_image') return 'collections_category2_image';
                if (fieldPath === 'collections_category3_image') return 'collections_category3_image';

                // Template 15 - Video Section thumbnail
                if (fieldPath === 'video_section_thumbnail') return 'video_section_thumbnail';

                // Template 16 - Premium Services (no images)

                // Template 17 - Hero with Values image
                if (fieldPath === 'hero_with_values_image') return 'hero_with_values_image';

                // Template 17 - Category Showcase images
                if (fieldPath === 'category_showcase_image1') return 'category_showcase_image1';
                if (fieldPath === 'category_showcase_image2') return 'category_showcase_image2';
                if (fieldPath === 'category_showcase_image3') return 'category_showcase_image3';
                if (fieldPath === 'category_showcase_image4') return 'category_showcase_image4';

                // Template 18/20 - Craftsmanship Story image
                if (fieldPath === 'craftsmanship_story_image') return 'craftsmanship_story_image';

                // Template 18 - Video Story thumbnail
                if (fieldPath === 'video_story_thumbnail') return 'video_story_thumbnail';

                // Template 18 - Category Grid images
                if (fieldPath === 'category_grid_image1') return 'category_grid_image1';
                if (fieldPath === 'category_grid_image2') return 'category_grid_image2';
                if (fieldPath === 'category_grid_image3') return 'category_grid_image3';

                // Template 18 - Quality Craftsmanship image
                if (fieldPath === 'quality_craftsmanship_image') return 'quality_craftsmanship_image';

                // Template 19 - Featured Stories images (3 stories)
                if (fieldPath === 'featured_story1_image') return 'featured_story1_image';
                if (fieldPath === 'featured_story2_image') return 'featured_story2_image';
                if (fieldPath === 'featured_story3_image') return 'featured_story3_image';

                // Template 19 - Editorial Story image
                if (fieldPath === 'editorial_story_image') return 'editorial_story_image';

                // Template 19 - Magazine Hero image
                if (fieldPath === 'magazine_hero_image') return 'magazine_hero_image';

                // Template 20 - Portfolio Gallery images (6 images)
                if (fieldPath === 'portfolio_gallery1_image') return 'portfolio_gallery1_image';
                if (fieldPath === 'portfolio_gallery2_image') return 'portfolio_gallery2_image';
                if (fieldPath === 'portfolio_gallery3_image') return 'portfolio_gallery3_image';
                if (fieldPath === 'portfolio_gallery4_image') return 'portfolio_gallery4_image';
                if (fieldPath === 'portfolio_gallery5_image') return 'portfolio_gallery5_image';
                if (fieldPath === 'portfolio_gallery6_image') return 'portfolio_gallery6_image';

                // Template 20 - Creative Process images (3 steps)
                if (fieldPath === 'creative_process1_image') return 'creative_process1_image';
                if (fieldPath === 'creative_process2_image') return 'creative_process2_image';
                if (fieldPath === 'creative_process3_image') return 'creative_process3_image';

                // Template - Craftsmanship image (general)
                if (fieldPath === 'craftsmanship_image') return 'craftsmanship_image';

                // Default: return the original field path (fallback)
                console.warn(`⚠️ No mapping found for field path: ${fieldPath}`);
                return fieldPath.replace(/\./g, '_');
            }

            // Generate user-filled details summary
            const generateUserSummary = (data) => {
                let summary = [];

                // Brand information
                if (data.content?.brand?.name) {
                    summary.push(`<strong>Brand:</strong> ${data.content.brand.name}`);
                }

                // Categories
                if (data.content?.categories?.length > 0) {
                    const categoryNames = data.content.categories.map((cat, idx) =>
                        `${idx + 1}. ${cat.name || 'Unnamed Category'}`
                    ).join('<br>&nbsp;&nbsp;&nbsp;&nbsp;');
                    summary.push(`<strong>Categories (${data.content.categories.length}):</strong><br>&nbsp;&nbsp;&nbsp;&nbsp;${categoryNames}`);
                }

                // Videos
                if (data.content?.videos?.length > 0) {
                    const videoTitles = data.content.videos.map((video, idx) =>
                        `${idx + 1}. ${video.title || 'Unnamed Video'}`
                    ).join('<br>&nbsp;&nbsp;&nbsp;&nbsp;');
                    summary.push(`<strong>Videos (${data.content.videos.length}):</strong><br>&nbsp;&nbsp;&nbsp;&nbsp;${videoTitles}`);
                }

                // Other array sections
                ['sliding_banners', 'promo_banners', 'fashion_trends', 'banners', 'collections', 'gallery', 'instagram'].forEach(section => {
                    const sectionData = data.content?.[section];
                    if (sectionData?.length > 0) {
                        const sectionName = section.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
                        const items = sectionData.map((item, idx) =>
                            `${idx + 1}. ${item.title || item.name || item.text || 'Unnamed Item'}`
                        ).join('<br>&nbsp;&nbsp;&nbsp;&nbsp;');
                        summary.push(`<strong>${sectionName} (${sectionData.length}):</strong><br>&nbsp;&nbsp;&nbsp;&nbsp;${items}`);
                    }
                });

                // Hero section
                if (data.content?.hero?.title) {
                    summary.push(`<strong>Hero Title:</strong> ${data.content.hero.title}`);
                }

                // Business info
                if (data.content?.business?.contact?.business_name) {
                    summary.push(`<strong>Business:</strong> ${data.content.business.contact.business_name}`);
                }

                // Other sections
                if (data.content?.statement?.title) {
                    summary.push(`<strong>Statement:</strong> ${data.content.statement.title}`);
                }

                if (data.content?.workshop?.title) {
                    summary.push(`<strong>Workshop:</strong> ${data.content.workshop.title}`);
                }

                return summary.join('<br><br>');
            };

            // Show user summary before submitting (use original formData, not cleanFormData)
            const userSummary = generateUserSummary(formData);

            const summaryResult = await Swal.fire({
                icon: 'info',
                title: 'Review Your Template Details',
                html: `
                    <div style="text-align: left; max-height: 400px; overflow-y: auto; padding: 10px; border: 1px solid #e5e7eb; border-radius: 8px; background-color: #f9fafb;">
                        <h4 style="margin-top: 0; color: #374151; font-weight: 600;">Template: ${selectedTemplate.name}</h4>
                        <hr style="margin: 10px 0; border-color: #d1d5db;">
                        ${userSummary || '<em style="color: #6b7280;">No content has been filled yet.</em>'}
                    </div>
                    <p style="margin-top: 15px; font-size: 14px; color: #6b7280;">
                        Do you want to proceed with creating this template?
                    </p>
                `,
                showCancelButton: true,
                confirmButtonText: 'Yes, Create Template',
                cancelButtonText: 'Go Back to Edit',
                confirmButtonColor: '#10b981',
                cancelButtonColor: '#6b7280',
                width: '600px'
            });

            if (!summaryResult.isConfirmed) {
                return; // User chose to go back and edit
            }

            // Submit to backend
            const response = await authAxios.post('/template-themes/', submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Success
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                html: `
                    <p>Template "<strong>${selectedTemplate.name}</strong>" has been created successfully!</p>
                    <div style="margin-top: 15px; padding: 10px; background-color: #f0f9ff; border-radius: 8px; font-size: 14px;">
                        <strong>Your template includes:</strong><br>
                        ${userSummary.split('<br><br>').slice(0, 3).join('<br>')}
                        ${userSummary.split('<br><br>').length > 3 ? '<br>...and more' : ''}
                    </div>
                `,
                confirmButtonColor: '#10b981'
            });

            // Refresh status
            const statusResponse = await authAxios.get('/template-themes/check-status/');
            if (statusResponse.data.success && statusResponse.data.data.has_template) {
                setHasExistingTemplate(true);
                setUserTemplateData(statusResponse.data.data);
                setCurrentStep('view_data');
            } else {
                setCurrentStep('select');
            }

        } catch (error) {
            console.error('Error creating template:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to create template. Please try again.',
                confirmButtonColor: '#f97316'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Modern template selection UI
    const renderTemplateSelection = () => (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
            <div className="container mx-auto px-4 py-12">
                {/* Existing template notice */}
                {hasExistingTemplate && (
                    <div className="max-w-4xl mx-auto mb-12">
                        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-8 text-white text-center">
                            <Star className="w-12 h-12 mx-auto mb-4" />
                            <h3 className="text-2xl font-bold mb-3">Update Your Template</h3>
                            <p className="text-blue-100 mb-6">
                                You already have a template configured. Select a new template below to update your design,
                                or view your current template.
                            </p>
                            <button
                                onClick={() => setCurrentStep('view_data')}
                                className="bg-white text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                            >
                                View Current Template
                            </button>
                        </div>
                    </div>
                )}

                {/* Enhanced Template Selector with Search & Filter */}
                <TemplateSelector
                    templates={templates}
                    selectedTemplate={selectedTemplate}
                    onSelect={handleTemplateSelect}
                />
            </div>
        </div>
    );

    // Enhanced configuration UI with new wrapper
    const renderConfiguration = () => {
        return (
            <EnhancedTemplateBuilder
                selectedTemplate={selectedTemplate}
                formData={formData}
                currentSection={currentSection}
                sections={getTemplateSections()}
                completedSections={completedSections}
                validationErrors={validationErrors}
                isSubmitting={isSubmitting}
                onBack={() => setCurrentStep('select')}
                onSectionChange={setCurrentSection}
                onPreview={() => setShowPreviewModal(true)}
                onSubmit={handleSubmit}
            >
                {/* All existing form content stays here */}
                {renderCurrentSectionContent()}
            </EnhancedTemplateBuilder>
        );
    };

    // Loading state
    if (isLoading) {
        return (
            <Base Content={
                <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                            <Layout className="w-8 h-8 text-white" />
                        </div>
                        <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Templates...</h2>
                        <p className="text-gray-600">Setting up your template creation experience</p>
                    </div>
                </div>
            } />
        );
    }

    // Main render
    return (
        <Base Content={
            <>
                {currentStep === 'select' && renderTemplateSelection()}
                {currentStep === 'configure' && renderConfiguration()}
                {currentStep === 'view_data' && (
                    <div className="min-h-screen bg-gray-50">
                        <div className="container mx-auto px-4 py-8">
                            <div className="text-center">
                                <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Template Data</h2>
                                <p className="text-gray-600 mb-8">View and manage your template configuration</p>
                                <button
                                    onClick={() => {
                                        // Populate form with existing data when editing
                                        populateFormWithExistingData();
                                        setCurrentStep('configure');
                                        setCurrentSection('brand');
                                    }}
                                    className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
                                >
                                    Edit Template
                                </button>
                                <button
                                    onClick={() => setCurrentStep('select')}
                                    className="ml-4 bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
                                >
                                    Change Template
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Real-time Preview Modal */}
                <PreviewModal />
            </>
        } />
    );
};

export default TemplateThemes;